import { Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';

import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AddEditService, AddEditSaveArgs } from './add-edit.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { SubmissionService } from '../../submission-data/submission.service';
import { applySubmissionSidebarHidden as setSubmissionShellSidebarHidden } from '../../submission-data/submission-detail/submission-sidebar-layout.util';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
/**
 * Entity-tree node shape produced by SubmissionController.loadHierarchy
 * (action_code=71n7lj). Field names are SHOUTED because the backend
 * SP returns them that way; renaming will break the legacy save payload
 * which echoes KEY_ID / ENTITY_TYPE_ID back to the server.
 */
interface EntityNode {
  KEY_DESC: string;
  PARENT: string;
  IS_LEAF: number;
  IS_ENTITY: number;
  LVL: number;
  PATH?: string;
  KEY_ID?: any;
  ENTITY_TYPE_ID?: any;
  hidden?: boolean;
  childrenHidden?: boolean;
  active?: boolean;
  [key: string]: any;
}

/**
 * Hierarchical row produced by `buildFormTree` (port of
 * AddEditController.loadForm L2190-L2325). One of:
 *   - leaf: editable element with ELEMENTVALUE
 *   - header: intermediate dotted-path node, no input
 *   - table: header-with-repeated-data, renders as a sub-table
 */
interface FormTreeRow {
  kind: 'leaf' | 'header' | 'table';
  ELEMENTNAME: string;
  FULLELEMENTNAME: string;
  level: number;
  // leaf fields
  ELEMENTVALUE?: any;
  LINENO?: string;
  SNO?: string;
  ENUMVALUES?: string[];
  MAXLENGTH?: number;
  VALIDATIONPATTERN?: string;
  VALIDATIONEXPLANATION?: string;
  isRequired?: boolean;
  IS_CHANGED?: 'Y' | 'N';
  IS_EDITABLE?: string;
  /** Rich tooltip metadata (legacy addEdit.html L585–590, L617–623). */
  DESCRIPTION?: string;
  TYPESCHEMA?: string;
  CHOICEGROUPID?: any;
  CHOICESEQ?: any;
  // table fields
  tableHeaders?: TableHeader[];
  repeatedRows?: RepeatedRow[];   // ordered for stable render
  repeatedRowsBySno?: Record<string, RepeatedRow>;
}

interface TableHeader {
  nodeName: string;
  fullElementName: string;
  isRequired: boolean;
  ENUMVALUES?: string[];
  MAXLENGTH?: number;
  /** Legacy grouping marker for child-popup columns (createTableHeader). */
  parentNodeName?: string | null;
  /** Legacy flag: when true, render input + expander in same column. */
  selfEdit?: boolean;
  hide?: boolean;
  /** This column's cells are themselves a nested table (inner-grid popup). */
  isNested?: boolean;
  DESCRIPTION?: string;
  TYPESCHEMA?: string;
  CHOICEGROUPID?: any;
  CHOICESEQ?: any;
}

interface RepeatedRow {
  SNO: string;
  MAX_OCCURS?: string;
  cells: Record<string, RepeatedCell>;
  toDelete?: boolean;  // soft-delete flag — port of legacy row.toDelete
  isNew?: boolean;     // marks rows added through addRepeatedRow
  /** Legacy repeated row: `IS_REPEATED` gates SNO + add icon in inner modal (add-edit-inner-grid.html L668). */
  IS_REPEATED?: boolean;
}

interface RepeatedCell {
  ELEMENTNAME: string;
  FULLELEMENTNAME: string;
  ELEMENTVALUE: any;
  SNO: string;
  MAXLENGTH?: number;
  ENUMVALUES?: string[];
  isRequired?: boolean;
  IS_CHANGED?: 'Y' | 'N';
  /** Legacy `ng-pattern` / `VALIDATIONPATTERN` from s3hr77 row (addEdit.html L634). */
  VALIDATIONPATTERN?: string;
  /** Shown on `fa-info-circle` when pattern invalid + touched (addEdit.html L603, L633). */
  VALIDATIONEXPLANATION?: string;
  /**
   * Nested sub-table — port of `createPopupElement` (L2845-L2896). When
   * present, this cell does not hold a scalar value; instead it renders as
   * an "Edit (N)" button that opens the inner-grid popup.
   */
  nestedTable?: NestedTable;
}

/**
 * Inner-grid table — same shape as the outer repeated table but lives on a
 * single cell. Mirrors `repData[parentNode]` from
 * AddEditController.createPopupElement / addOrOpenChildRow (L2343, L2845).
 */
interface NestedTable {
  parentSno: string;        // owning row's SNO
  parentColumn: string;     // column name in outer table (popup title)
  fullElementName: string;  // breadcrumb-friendly full path
  tableHeaders: TableHeader[];
  repeatedRows: RepeatedRow[];
  repeatedRowsBySno: Record<string, RepeatedRow>;
}

/**
 * Anything we can run addRepeatedRow / toggleDeleteRow against. Both
 * `FormTreeRow` of kind=table and `NestedTable` satisfy this shape.
 */
interface RepeatedTableLike {
  tableHeaders?: TableHeader[];
  repeatedRows?: RepeatedRow[];
  repeatedRowsBySno?: Record<string, RepeatedRow>;
}

const JCD_KEY_DEFAULT = 250;
const DOT = '.';

/** Tab key for nested `gtw-efile-elements-data` (mirrors `ElementsDataTabKey`). */
export type EfileAddEditDataTabKey = 'data' | 'pdf' | 'overrides' | 'xml';

@Component({
    selector: 'gtw-efile-add-edit',
    templateUrl: './add-edit.component.html',
    styleUrls: ['./add-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileAddEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  /** Bound to nested `gtw-efile-elements-data` (submission shell sets `'data'`). */
  @Input() activeTab: EfileAddEditDataTabKey = 'data';

  @Input()
  set submissionMenuOpen(open: boolean) {
    const next = !!open;
    if (this.submissionShellMenuOpen === next) {
      return;
    }
    this.submissionShellMenuOpen = next;
    this.syncParentSidebarWithAside();
    this.cdr.markForCheck();
  }

  @Output() submissionMenuOpenChange = new EventEmitter<boolean>();

  /** Forwards nested shell tab changes to `submission-detail` / router. */
  @Output() tabChange = new EventEmitter<{ subState: string; urlSegments: string[] }>();

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  // ---------------------------------------------------------------------------
  // Entity-tree state (legacy SubmissionController L488-L633)
  // ---------------------------------------------------------------------------
  entityTree: EntityNode[] = [];
  private entityTreeCopy: EntityNode[] = [];
  loadingKeys = false;
  showHierarchy = true;
  searchTerm = '';
  selectedEntity: EntityNode | null = null;

  /**
   * When true, the submission shell left menu (`#sidebar-container`) is visible
   * so the user can switch to Dashboard, PDF, Return Review, etc. Toggled from
   * the hierarchy header chevron (expected UX per e-file Add/Edit screenshots).
   */
  submissionShellMenuOpen = false;

  // ---------------------------------------------------------------------------
  // Form-tile + editor state (legacy AddEditController L2120+)
  // ---------------------------------------------------------------------------
  /**
   * When true with `showData`, the DATA tile strip is fully collapsed so the
   * editor uses the full width — port of legacy `ctrl.expandView` + `.card.closed`
   * (`addEdit.html` L444, `data.html` L93–96) toggled by `efile:expand-view`.
   */
  expandView = false;
  showData = false;
  showFormLoader = false;
  loadingForms = false;
  loadingTree = false;
  saving = false;
  lockEdit = false;
  isProductionAccepted = false;
  /**
   * Submission-level IS_LOCKED — port of `selectedSubmissionData.IS_LOCKED`
   * (addEdit.html L491–L494, L562–L564). When true, the submission is locked
   * for editing as a whole; both Save AND Unlock buttons are hidden and the
   * "Submission is Locked." banner is shown. This is distinct from
   * `lockEdit`, which is the form-source lock returned by `7bkpvk`.
   */
  isSubmissionLocked = false;

  forms: any[] = [];        // raw forms (FORM × FORM_SUB rows) for selected entity
  uniqForms: any[] = [];    // de-duplicated by FORM (the tile list)
  allForms: any[] = [];     // schema-set forms (used when an unsaved form is opened)
  private formsMap: Record<string, any[]> = {};

  formSubOptions: any[] = [];
  parentElementNames: any[] = [];

  selectedForm = '';
  selectedFormSub = '';
  selectedParentElement = '';
  formSearchTxt = '';
  addMode = false;
  loadingSubs = false;
  loadingParentElems = false;
  formId = '';

  treeData: any[] = [];     // raw element rows from action s3hr77
  formTree: FormTreeRow[] = []; // hierarchical port of legacy vm.formTree
  changedFields: any[] = []; // collected for clob_data.changes

  /**
   * Keys `${FULLELEMENTNAME}_${SNO}` for fields that have blurred — mirrors
   * AngularJS `$touched` so validation UI matches addEdit.html (L603, L633).
   */
  private readonly validationTouchKeys = new Set<string>();

  // ---------------------------------------------------------------------------
  // Pre-save comments modal — port of AddEditController.askComment (L2494)
  // ---------------------------------------------------------------------------
  showCommentsModal = false;

  // Inner-grid popup — port of AddEditController.addOrOpenChildRow (L2343).
  // When set, we render a modal overlay showing the nested table editor
  // for that parent row's cell.
  nestedPopup: {
    parentRow: RepeatedRow;
    parentColumn: string;
    nestedTable: NestedTable;
    title: string;
  } | null = null;

  // ---------------------------------------------------------------------------
  // Submission context (mirrors EfileFactory.selectedSubmission)
  // ---------------------------------------------------------------------------
  submissionId: any = '';
  keyIdFromRow: any = '';   // fallback if user never picks an entity
  entityTypeIdFromRow: any = '';
  schemaSetId: any = '';
  taxYear: any = '';
  ssoId = '';
  modalTitle = 'Add / Edit Form Data';

  // Banner messages — legacy flags surfaced as inline text (no notification svc).
  // Copy ports legacy `addEdit.html` L491-L494 exactly so both spans can co-exist
  // inside a single `.highlighted-text` strip when IS_LOCKED + PRODUCTION_ACCEPTED
  // are both true.
  readonly PROD_ACCEPTED_MESSAGE = 'The Submission is Production Accepted.';
  readonly LOCK_EDIT_MESSAGE =
    'This form is currently locked for editing for your source.This form is currently set to readonly mode. To make edits, please click the unlock button to enable editing.';
  /** Legacy `addEdit.html` L492 banner copy for `selectedSubmissionData.IS_LOCKED`. */
  readonly SUBMISSION_LOCKED_MESSAGE = 'The Submission is Locked.';

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private addEditService: AddEditService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
    private route: ActivatedRoute,
    private readonly elRef: ElementRef<HTMLElement>,
  ) { }

  // ---------------------------------------------------------------------------
  // Init / context wiring
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.syncParentSidebarWithAside();
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.submissionShellMenuOpen = false;
    setSubmissionShellSidebarHidden(false, this.submissionShellScope());
    this.subs.unsubscribe();
  }

  onDataShellTabChange(payload: { subState: string; urlSegments: string[] }): void {
    this.tabChange.emit(payload);
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    // Prefer SubmissionService.activeSubmission — that's what the URL
    // `/submissions-dasboard/submission/:id/data/add-edit` activates when the
    // user clicks Add/Edit from the sidebar (or follows a deep-link).
    // The legacy controller (`AddEditController` in
    // `ui/app/components/efile/efileElements/data/AddEditCtrl.js`) read its
    // submission row from `EfileFactory.selectedSubmission`, which is the
    // direct equivalent of `SubmissionService.activeSubmission`.
    // The workspace activeScreen rowData fallback is only useful when this
    // component is hosted as a CE inside the legacy AngularJS shell.
    const activeSubmission: any = this.submissionService?.activeSubmission;
    if (activeSubmission && activeSubmission.SUBMISSION_ID) {
      this.rowData = activeSubmission;
    } else {
      this.rowData = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen)) || {};
    }
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    if (!this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      this.ssoId = this.USER_SETTINGS?.user?.sso_id || '';
      // Add/Edit can be deep-linked directly via
      // `/submissions-dasboard/submission/:id/data/add-edit`.
      // In that flow, rowData may not be hydrated yet on first paint, so
      // fall back to route `:id` to keep hierarchy/form loading functional.
      this.submissionId =
        this.rowData.SUBMISSION_ID ||
        this.rowData.submission_id ||
        this.route.snapshot.params?.['id'] ||
        '';
      this.keyIdFromRow = this.rowData.KEY_ID || this.rowData.key_id || '';
      this.entityTypeIdFromRow = this.rowData.ENTITY_TYPE_ID || this.rowData.entity_type_id || '';
      this.schemaSetId = this.rowData.SCHEMA_SET_ID || this.rowData.schema_set_id || '';
      this.taxYear = this.rowData.TAX_YEAR || this.filterParams?.tax_year || '';
      this.isProductionAccepted =
        this.rowData.IS_PRODUCTION_ACCEPTED === 'Y' ||
        this.rowData.PROD_ACCEPTED === 'Y' ||
        this.rowData.SUBMISSION_STATUS === 'Production Accepted';
      // Submission-level IS_LOCKED flag — port of legacy
      // `EfileFactory.selectedSubmission.IS_LOCKED` consumed by addEdit.html
      // L491-L494 and L562-L564. Truthy values from the SP can be 'Y',
      // 1, true, or the string '1' depending on the JCD lookup, so accept
      // any truthy non-'N' representation.
      const lockRaw =
        this.rowData.IS_LOCKED ?? this.rowData.is_locked ?? null;
      this.isSubmissionLocked =
        lockRaw !== null &&
        lockRaw !== '' &&
        lockRaw !== 'N' &&
        lockRaw !== 0 &&
        lockRaw !== '0' &&
        lockRaw !== false;

      this.loadHierarchy();
    } catch (e) {
      console.error('[EfileAddEdit] init failed', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Entity hierarchy (left pane)  — port of SubmissionController L521-L603
  // ---------------------------------------------------------------------------

  private getScenario(): string | number {
    // 1) Primary source: active screen filters
    const filterScenario = this.filterParams?.scenario ?? this.filterParams?.scenario_key;
    if (filterScenario !== undefined && filterScenario !== null && String(filterScenario) !== '') {
      return filterScenario;
    }

    // 2) Fallback to submission/global context (same pattern used in other efile screens)
    const contextScenario =
      this.rowData?.SCENARIO ??
      this.rowData?.scenario ??
      this.globalService?.scenario ??
      this.globalService?.globalParams?.scenario;
    if (contextScenario !== undefined && contextScenario !== null && String(contextScenario) !== '') {
      return contextScenario;
    }

    // 3) Last fallback: tax-year -> scenario map from workspace inputs
    const taxYearVal =
      this.taxYear ??
      this.rowData?.TAX_YEAR ??
      this.filterParams?.tax_year ??
      this.globalService?.tax_year ??
      this.globalService?.globalParams?.tax_year;
    const values =
      (this.globalService as any)?.inputs?.tax_year?.values ||
      (this.gtwGlobal as any)?.global?.inputs?.tax_year?.values ||
      [];
    const found = values.find((v: any) => String(v?.value) === String(taxYearVal ?? ''));
    if (found?.scenario !== undefined && found?.scenario !== null && String(found.scenario) !== '') {
      return found.scenario;
    }

    return '';
  }

  loadHierarchy(): void {
    if (!this.submissionId || !this.taxYear) return;
    this.loadingKeys = true;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    const scenario = this.getScenario();
    const hierarchyParams = this.buildHierarchyParams(scenario);
    this.cdr.markForCheck();

    this.addEditService
      .loadHierarchy(
        baseUrl,
        this.submissionId,
        this.taxYear,
        scenario,
        hierarchyParams.jcd_key,
        this.ssoId,
        hierarchyParams,
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          this.loadingKeys = false;
          const raw = response?.jsonObject || [];
          this.entityTree = this.sanitizeHierarchy(raw);
          this.entityTreeCopy = _.cloneDeep(this.entityTree);
          this.maybeAutoSelectFromRow();
          this.cdr.markForCheck();
        },
        (err: any) => {
          console.error('[EfileAddEdit] loadHierarchy failed', err);
          this.loadingKeys = false;
          this.cdr.markForCheck();
        }
      );
  }

  private buildHierarchyParams(scenario: string | number): Record<string, any> {
    const customCtx = this.getCustomScreenContext();
    const g = this.globalService || {};
    const f = this.filterParams || {};
    const issueId = f.issue_id_for_tx_save ?? g.issue_id_for_tx_save ?? f.issue_id ?? g.issue_id ?? '';

    return {
      activity_key: f.activity_key ?? g.activity_key ?? customCtx.activity_key ?? '',
      is_locked: f.is_locked ?? g.is_locked ?? 'N',
      issue_id: issueId,
      issue_id_for_tx_save: issueId,
      issue_short_desc: f.issue_short_desc ?? g.issue_short_desc ?? '',
      jcd_key: f.jcd_key ?? g.jcd_key ?? this.rowData?.JCD_KEY ?? JCD_KEY_DEFAULT,
      jcd_ta_key: f.jcd_ta_key ?? g.jcd_ta_key ?? customCtx.jcd_ta_key ?? '',
      scenario,
      screen_key: f.screen_key ?? g.screen_key ?? customCtx.screen_key ?? '',
      screen_type: f.screen_type ?? g.screen_type ?? customCtx.screen_type ?? 'custom',
      submission_id: this.submissionId,
      tax_year: this.taxYear,
      sso_id: this.ssoId,
    };
  }

  /**
   * Source custom screen context from activeScreen first, then filter/global params.
   */
  private getCustomScreenContext(): Record<string, any> {
    const screen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    return {
      activity_key:
        screen?.activity_key ??
        screen?.ACTIVITY_KEY ??
        screen?.parent?.activity_key ??
        screen?.parent?.ACTIVITY_KEY ??
        '',
      screen_key: screen?.screen_key ?? screen?.SCREEN_KEY ?? '',
      screen_type: screen?.screen_type ?? screen?.SCREEN_TYPE ?? 'custom',
      jcd_ta_key:
        screen?.jcd_ta_key ??
        screen?.JCD_TA_KEY ??
        screen?.data?.selected?.rowData?.JCD_TA_KEY ??
        '',
    };
  }

  /**
   * Two-pass sanitation lifted from SubmissionController.sanatize (L537).
   * Removes "folders acting as leaves" (IS_LEAF && !IS_ENTITY) and promotes
   * their parents to leaves. Pure function — does not mutate the input.
   */
  private sanitizeHierarchy(input: EntityNode[]): EntityNode[] {
    let data: EntityNode[] = (input || []).map(n => ({ ...n }));
    for (let pass = 0; pass < 2; pass++) {
      data = data.filter((item: EntityNode) => {
        if (item.IS_LEAF && !item.IS_ENTITY) {
          const parent = data.find(d => d.KEY_DESC === item.PARENT);
          if (parent) parent.IS_LEAF = 1;
        }
        return !(item.IS_LEAF && !item.IS_ENTITY);
      });
    }
    return data;
  }

  /**
   * If the submission row already pins us to a specific entity (KEY_ID +
   * ENTITY_TYPE_ID), select it programmatically so users land directly on
   * the form tiles — matches the behaviour of opening a row that already
   * carries entity context.
   */
  private maybeAutoSelectFromRow(): void {
    if (!this.keyIdFromRow) return;
    const node = this.entityTree.find(
      n => String(n.KEY_ID) === String(this.keyIdFromRow) && n.IS_LEAF === 1
    );
    if (node) {
      this.entitySelected(node);
    }
  }

  /**
   * Hierarchy header chevron: show or hide the submission left menu
   * (`#sidebar-container`) so users can leave Add/Edit or open sibling states.
   */
  toggleSubmissionShellMenu(): void {
    this.submissionShellMenuOpen = !this.submissionShellMenuOpen;
    this.syncParentSidebarWithAside();
    this.submissionMenuOpenChange.emit(this.submissionShellMenuOpen);
    this.cdr.markForCheck();
  }

  /**
   * Legacy `AddEditController` listener for `$scope.$on('efile:expand-view')`
   * (EfileElementsController.js L2134–2136). Also invoked from the tab toolbar
   * (`gtw-efile-elements-data`) via `@ContentChild`.
   */
  toggleExpandView(): void {
    this.expandView = !this.expandView;
    this.cdr.markForCheck();
  }

  /**
   * Keeps `#sidebar-container` visibility in sync with `submissionShellMenuOpen`.
   * Default is hidden for maximum form width; user opens it via the hierarchy chevron.
   */
  private syncParentSidebarWithAside(): void {
    setSubmissionShellSidebarHidden(!this.submissionShellMenuOpen, this.submissionShellScope());
  }

  /** Same `<submission-detail>` instance that owns `#sidebar-container`. */
  private submissionShellScope(): HTMLElement | null {
    return this.elRef.nativeElement?.closest?.('submission-detail') ?? null;
  }

  /**
   * Entity click — port of SubmissionController.entitySelected (L553) and
   * AddEditController's `efile:entity-selected` listener
   * ([EfileElementsController.js](ui/app/components/efile/efileElements/EfileElementsController.js) L1921-L1940).
   * Folder nodes (IS_LEAF === 0) toggle expand/collapse. Leaf nodes
   * become the active entity and trigger form loading.
   *
   * Legacy parity (controller L1923-L1925): early-return when the user
   * re-clicks the same entity type so we don't re-fire the form lookups
   * (`jvpk52` + `onozya`) and the source-lock check on every click.
   */
  entitySelected(node: EntityNode): void {
    if (node.IS_LEAF === 0) {
      this.toggle(this.entityTree, node);
      return;
    }
    // Legacy parity (`$broadcast('efile:entity-selected', node)`): every
    // leaf click runs the entity-selection flow so the form pane resets
    // and the per-entity form lookups re-fire. The previous same-node
    // guard suppressed selection when both ENTITY_TYPE_ID *and* KEY_ID
    // matched, which left the migrated lock-edit banner (and stale
    // form filters / form tree) stuck on screen when the user clicked
    // the same node again to recover. Removing that guard matches the
    // legacy controller, which always invoked `selectedEntity = node`
    // in the listener and re-hydrated the form lookups.
    const lastActive = this.entityTree.find(n => n.active);
    if (lastActive) lastActive.active = false;
    node.active = true;
    this.selectedEntity = node;
    this.showData = false;
    this.expandView = false;
    this.syncParentSidebarWithAside();
    // Full reset of form-pane state on entity change — without these
    // the migrated UI carried over the previous entity's lock banner,
    // form id, dropdown selections, formTree, changed fields,
    // validation touch state, and any open inner-grid popup. Legacy
    // `clickIcon` and the entity-selected listener cleared the
    // equivalent state on every entity switch (controller L2520-L2543,
    // L1921-L1940), so mirror that here.
    this.selectedForm = '';
    this.selectedFormSub = '';
    this.selectedParentElement = '';
    this.formSubOptions = [];
    this.parentElementNames = [];
    this.formId = '';
    this.lockEdit = false;
    this.addMode = false;
    this.treeData = [];
    this.formTree = [];
    this.changedFields = [];
    this.validationTouchKeys.clear();
    this.nestedPopup = null;
    // De-activate prior tile so the next form-pane render does not
    // flash the previous entity's "active" tile while the new forms
    // load (legacy parity with `clickIcon`'s active-tile swap).
    if (this.uniqForms?.length) {
      const prevActiveTile = this.uniqForms.find(f => f.isActive);
      if (prevActiveTile) prevActiveTile.isActive = false;
    }

    this.loadFormsForEntity();
    this.loadAllSchemaForms();
    this.cdr.markForCheck();
  }

  /**
   * Direct port of SubmissionController.toggle (L568-L603). Walks the flat
   * tree array and toggles `hidden`/`childrenHidden` flags for descendants
   * based on LVL.
   */
  toggle(tree: EntityNode[], item: EntityNode): void {
    if (item.IS_LEAF === 1) return;
    const start = tree.indexOf(item) + 1;

    if (!item.childrenHidden) {
      item.childrenHidden = true;
      for (let i = start; i < tree.length; i++) {
        const prev = i - 1;
        if (tree[i].LVL <= item.LVL) return;
        tree[i].hidden = true;
        if (tree[prev].LVL < tree[i].LVL) {
          tree[prev].childrenHidden = true;
        }
      }
    } else {
      item.childrenHidden = false;
      for (let i = start; i < tree.length; i++) {
        if (tree[i].LVL <= item.LVL) return;
        if (tree[i].LVL - 1 === item.LVL) {
          tree[i].hidden = false;
        } else {
          tree[i].hidden = true;
        }
      }
    }
  }

  /**
   * Search filter — simplified port of filterTreeData (L606-L633). Empty
   * search restores the full tree from the cached copy.
   */
  filterTreeData(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.entityTree = _.cloneDeep(this.entityTreeCopy);
      this.cdr.markForCheck();
      return;
    }

    const tree = this.entityTreeCopy.map(n => ({
      ...n,
      hidden: true,
      childrenHidden: false,
    }));

    const visibleIndexes = new Set<number>();

    for (let i = 0; i < tree.length; i++) {
      const label = (tree[i].KEY_DESC || '').toLowerCase();
      if (!label.includes(term)) continue;

      // Keep the matched node.
      visibleIndexes.add(i);

      // Keep all ancestors so hierarchy path remains visible.
      let level = tree[i].LVL;
      for (let j = i - 1; j >= 0 && level > 0; j--) {
        if (tree[j].LVL < level) {
          visibleIndexes.add(j);
          level = tree[j].LVL;
        }
      }
    }

    // Show matched nodes + parent chain; keep others hidden.
    for (let i = 0; i < tree.length; i++) {
      const isVisible = visibleIndexes.has(i);
      tree[i].hidden = !isVisible;
      if (isVisible && tree[i].IS_LEAF === 0) {
        // Expand visible ancestors so the matched path is readable.
        tree[i].childrenHidden = false;
      }
    }

    this.entityTree = tree;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Form lookups (right pane)  — port of AddEditController L2146-L2174
  // ---------------------------------------------------------------------------

  private get currentKeyId(): any {
    return this.selectedEntity?.KEY_ID ?? this.keyIdFromRow;
  }

  private get currentEntityTypeId(): any {
    return this.selectedEntity?.ENTITY_TYPE_ID ?? this.entityTypeIdFromRow;
  }

  private loadFormsForEntity(): void {
    if (!this.submissionId || !this.currentKeyId) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    this.loadingForms = true;
    this.cdr.markForCheck();

    this.addEditService.loadFormsForSubmission(baseUrl, this.submissionId, this.currentKeyId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.forms = data?.jsonObject || [];
        this.uniqForms = _.uniqBy(this.forms, 'FORM');
        this.formsMap = _.groupBy(this.forms, 'FORM');
        this.loadingForms = false;
        this.cdr.markForCheck();
      },
      (err: any) => {
        console.error('[EfileAddEdit] loadFormsForSubmission failed', err);
        this.loadingForms = false;
        this.cdr.markForCheck();
      }
    );
  }

  private loadAllSchemaForms(): void {
    if (!this.currentEntityTypeId || !this.schemaSetId) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    // Legacy onozya payload parity (see captured request in
    // add-edit.service.ts loadAllForms doc): forward the same custom-screen
    // context the hierarchy lookup uses (`tax_year`, `submission_id`,
    // `screen_type`, `screen_key`, `scenario`, `jcd_ta_key`, `jcd_key`,
    // `issue_*`, `is_locked`, `activity_key`) and strip `sso_id` since
    // the legacy onozya call did not include it.
    const { sso_id: _sso, ...context } = this.buildHierarchyParams(this.getScenario());
    this.addEditService
      .loadAllForms(baseUrl, this.currentEntityTypeId, this.schemaSetId, context).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => { this.allForms = data?.jsonObject || []; this.cdr.markForCheck(); },
        (err: any) => console.error('[EfileAddEdit] loadAllForms failed', err)
      );
  }

  /**
   * DATA-tile click — port of AddEditController.clickIcon (L2771-L2830) for
   * the `view` mode (existing form). Add-form ('add' mode) is deferred.
   */
  selectForm(form: any): void {
    if (!form) return;
    this.addMode = false;
    this.showData = true;
    this.syncParentSidebarWithAside();

    // De-activate the prior tile + activate this one for the visual highlight.
    const prevActive = this.uniqForms.find(f => f.isActive);
    if (prevActive) prevActive.isActive = false;
    this.selectedForm = form.FORM || form.form || '';

    const clickedForm = this.uniqForms.find(f => f.FORM === this.selectedForm);
    if (clickedForm) clickedForm.isActive = true;

    // Lock-source check (legacy: loadLookup '7bkpvk').
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    const lockContext = this.buildHierarchyParams(this.getScenario());
    this.addEditService.checkLockSources(baseUrl, this.ssoId, lockContext).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const sources: any[] = data?.jsonObject || [];
        const formSource = form.SOURCE;
        this.lockEdit = !!(formSource && sources.findIndex((s: any) => s.SOURCE === formSource) > -1);
        this.cdr.markForCheck();
      },
      (err: any) => console.error('[EfileAddEdit] checkLockSources failed', err)
    );

    // Edit-mode (tile click) parity: legacy `clickIcon('edit', ...)`
    // (EfileElementsController.js L2572-L2573) ALWAYS auto-picked the
    // first `FORM_SUB` / first `PARENT_ELEMENT_NAME` from the form's
    // grouped row set so the editor opens immediately without forcing
    // the user to re-pick from a single-option dropdown. Use a
    // dedicated rebuild path that prepopulates regardless of option
    // count, instead of the length-1-only `rebuildFormFiltersForSelectedForm`
    // (which is for typeahead-driven cascades, not tile clicks).
    this.rebuildFormFiltersForEditMode();

    this.loadFormData();
  }

  onSelectedFormChange(): void {
    if (!this.selectedForm) {
      this.selectedFormSub = '';
      this.selectedParentElement = '';
      this.formSubOptions = [];
      this.parentElementNames = [];
      this.cdr.markForCheck();
      return;
    }
    if (this.addMode) {
      this.loadAddModeFilterOptions();
    } else {
      this.rebuildFormFiltersForSelectedForm();
    }
    this.cdr.markForCheck();
  }

  /**
   * `gtw-efile-typeahead-select` `(ngModelChange)` for the Form input —
   * legacy parity with `addEdit.html` L507's plain `ng-model` on the
   * `uib-typeahead` input. Fires on every keystroke as the user
   * free-types, so this MUST stay model-only — re-running the
   * dropdown 2 + dropdown 3 cascade per character would re-fetch
   * `loadFormSubOptions` / `loadParentElementNames` on every key
   * (add mode) or churn / clear `selectedFormSub` /
   * `selectedParentElement` per character (edit mode), neither of
   * which the legacy `uib-typeahead` did. Cascade is now triggered
   * exclusively by `(selectionChange)` -> `onTypeaheadFormSelected`,
   * which corresponds 1:1 with legacy `typeahead-on-select="ctrl.clickIcon('add', $item)"`
   * (addEdit.html L509).
   */
  onTypeaheadFormChange(v: string | null): void {
    this.selectedForm = v || '';
  }

  /**
   * `gtw-efile-typeahead-select` `(selectionChange)` for the Form input
   * — direct port of legacy `typeahead-on-select="ctrl.clickIcon('add', $item)"`
   * (`addEdit.html` L509). Fires only when the user commits an option
   * from the dropdown list (mouse click on a row or Enter on a
   * keyboard-highlighted row), never on plain typing or chevron
   * clear, so the cascade that loads dropdown 2 (Form Sub) and
   * dropdown 3 (Parent Element) only runs when the user has actually
   * settled on a Form value.
   */
  onTypeaheadFormSelected(v: string | null): void {
    this.selectedForm = v || '';
    this.onSelectedFormChange();
  }

  onTypeaheadFormSubChange(v: string | null): void {
    // Legacy parity (`addEdit.html` L524-L526) — the live `uib-typeahead`
    // for Form Sub has NO `ng-change` / `typeahead-on-select` handler
    // (the commented-out `<select>` at L505-L506 once did, but
    // `elemSelected` itself is commented out at `EfileElementsController.js`
    // L1951). Picking a different Form Sub must NOT auto-load the form
    // data — only the "Show Form" button click (L557
    // `ng-click="ctrl.loadForm()"`) and the form-tile edit click
    // (`clickIcon('edit', ...)` L2577 `vm.loadForm()`) should trigger
    // `loadFormData()`.
    this.selectedFormSub = v || '';
  }

  onTypeaheadParentElementChange(v: string | null): void {
    // Same legacy parity as `onTypeaheadFormSubChange` — the
    // `parent_element_name` typeahead (addEdit.html L544-L545) has no
    // `ng-change`, so we MUST NOT auto-load the form on manual change.
    this.selectedParentElement = v || '';
  }

  /**
   * "Show Form" button handler — direct port of `addEdit.html` L557
   *   `<button ng-click="ctrl.loadForm()"
   *            ng-disabled="!ctrl.selectedForm || !ctrl.form_sub || !ctrl.parent_element_name">`
   * Loads (or reloads) the form payload using the current filter trio.
   * The template's `[disabled]` binding guards the same condition, but
   * we keep the same guard here so programmatic callers don't trip an
   * empty load when the user hasn't picked all three filters yet.
   */
  showForm(): void {
    if (this.selectedForm && this.selectedFormSub && this.selectedParentElement) {
      this.loadFormData();
    }
  }

  /**
   * Legacy parity (controller L1987-L1993):
   *   let clickedForm = _.find(vm.uniqForms,["FORM",vm.selectedForm]);
   *   if (!clickedForm) {
   *     vm.forms.unshift({FORM:vm.selectedForm, isActive:true, unsaved:true});
   *   }
   *
   * Critically, legacy pushes the synthetic entry into `vm.forms`, NOT
   * `vm.uniqForms`. The tile rail (`addEdit.html` L466-L468) iterates
   * `ctrl.uniqForms`, so the synthetic "unsaved" entry is INVISIBLE in
   * legacy — no red "New" badge ever appears for an add-mode form load.
   * The only purpose of the entry is internal state tracking until
   * `vm.loadForms()` re-fetches after a successful save.
   *
   * Earlier the migration pushed into `uniqForms`, which produced the
   * "New" badge tile shown in the user-reported screenshot for forms
   * loaded via Show Form. Match legacy: push into `forms` only, leave
   * `uniqForms` untouched, and just deactivate any previously-active
   * tile so the editor pane is clearly "the active editor" without
   * a misleading tile highlight.
   *
   * Idempotent: re-invoking with the same form does NOT duplicate the
   * entry in either array.
   */
  private maybeFlagUnsavedForm(): void {
    if (!this.selectedForm) return;
    if (!this.uniqForms) this.uniqForms = [];
    if (!this.forms) this.forms = [];

    const existingTile = this.uniqForms.find(
      f => (f?.FORM ?? f?.form) === this.selectedForm
    );
    if (existingTile) {
      const prevActive = this.uniqForms.find(f => f !== existingTile && f.isActive);
      if (prevActive) prevActive.isActive = false;
      existingTile.isActive = true;
      return;
    }

    // Brand-new form (not in tile rail). De-activate any prior tile so
    // the rail doesn't show a stale highlight while the editor edits a
    // form that has no corresponding tile yet.
    const prevActive = this.uniqForms.find(f => f.isActive);
    if (prevActive) prevActive.isActive = false;

    // Mirror legacy `vm.forms.unshift(...)` for internal tracking only.
    // Skip when an entry already exists so repeated reloads don't pile
    // up duplicates in the raw forms array.
    const alreadyTracked = this.forms.some(
      f => (f?.FORM ?? f?.form) === this.selectedForm
    );
    if (!alreadyTracked) {
      this.forms = [
        { FORM: this.selectedForm, isActive: true, unsaved: true },
        ...this.forms,
      ];
    }
  }

  /**
   * Edit-mode form switch — port of legacy `clickIcon('edit', ...)`
   * (EfileElementsController.js L2563-L2578).
   *
   * Legacy edit-mode (L2572-L2573) always picked `[0]` for both cascading
   * filters. Product requirement now overrides that: dropdowns 2
   * (`form_sub`) and 3 (`parent_element_name`) must auto-select ONLY when
   * the option list collapses to exactly one entry — same `length === 1`
   * guard already used by add-mode `loadAddModeFilterOptions`
   * (legacy `clickIcon('add', ...)` L2550-L2552 / L2559-L2562). When
   * multiple options exist the user must pick explicitly via the
   * typeahead, mirroring add-mode behavior.
   *
   * CASCADING DEPENDENCY: dropdown 3 (`parent_element_name`) must NEVER
   * auto-select while dropdown 2 (`form_sub`) is still empty — the user
   * would otherwise see dropdown 3 pre-filled with a value while
   * dropdown 2 prompts them to choose, which is the inverted-cascade
   * bug they flagged. So parent-element auto-pick is gated on BOTH
   * `selectedFormSub` being non-empty AND `parentElementNames.length === 1`.
   *
   * Order: `form_sub` is computed BEFORE `parent_element_name` so the
   * earlier dropdown is populated first (matches legacy assignment
   * order at L2572-L2573) and the cascade gate above reads its post-
   * assignment value.
   */
  private rebuildFormFiltersForSelectedForm(): void {
    this.selectedFormSub = '';
    this.selectedParentElement = '';
    const sameForm = this.formsMap[this.selectedForm] || [];
    this.formSubOptions = _.uniqBy(
      sameForm.map(r => ({ FORM_SUB: r.FORM_SUB })).filter(r => !!r.FORM_SUB),
      'FORM_SUB'
    );
    this.parentElementNames = _.uniqBy(
      sameForm.map(r => ({ PARENT_ELEMENT_NAME: r.PARENT_ELEMENT_NAME })).filter(r => !!r.PARENT_ELEMENT_NAME),
      'PARENT_ELEMENT_NAME'
    );
    // Auto-select only when exactly one option exists (length === 1
    // guard; see method-level docblock for the legacy-vs-product
    // requirement rationale).
    this.selectedFormSub =
      this.formSubOptions.length === 1
        ? this.formSubOptions[0]?.FORM_SUB || ''
        : '';
    // Cascade gate: parent-element auto-pick requires form-sub to have
    // already auto-selected. If form-sub stayed empty (length > 1),
    // dropdown 3 stays empty regardless of its own option count.
    this.selectedParentElement =
      this.selectedFormSub && this.parentElementNames.length === 1
        ? this.parentElementNames[0]?.PARENT_ELEMENT_NAME || ''
        : '';
  }

  /**
   * Edit-mode rebuild path for {@link selectForm} (tile click). Direct
   * port of legacy `clickIcon('edit', form)` (EfileElementsController.js
   * L2572-L2573) which always assigned the first `FORM_SUB` and first
   * `PARENT_ELEMENT_NAME` from the form's grouped rows so `loadForm()`
   * runs immediately without the user having to confirm a single-option
   * dropdown choice. Mirrors the legacy assignments verbatim:
   *   vm.form_sub = vm.form_subs[0].FORM_SUB;
   *   vm.parent_element_name = vm.parent_elems[0].PARENT_ELEMENT_NAME;
   *
   * Distinct from {@link rebuildFormFiltersForSelectedForm}, which is
   * driven by the typeahead cascade and only auto-picks single-option
   * lists. The two paths differ because tile clicks already represent
   * a complete user choice (the tile IS the form), while typeahead
   * changes are mid-flight edits where premature auto-pick produced
   * the inverted-cascade bug previously flagged.
   */
  private rebuildFormFiltersForEditMode(): void {
    this.selectedFormSub = '';
    this.selectedParentElement = '';
    const sameForm = this.formsMap[this.selectedForm] || [];
    this.formSubOptions = _.uniqBy(
      sameForm.map(r => ({ FORM_SUB: r.FORM_SUB })).filter(r => !!r.FORM_SUB),
      'FORM_SUB'
    );
    this.parentElementNames = _.uniqBy(
      sameForm.map(r => ({ PARENT_ELEMENT_NAME: r.PARENT_ELEMENT_NAME })).filter(r => !!r.PARENT_ELEMENT_NAME),
      'PARENT_ELEMENT_NAME'
    );
    this.selectedFormSub = this.formSubOptions[0]?.FORM_SUB || '';
    this.selectedParentElement =
      this.parentElementNames[0]?.PARENT_ELEMENT_NAME || '';
  }

  startAddForm(): void {
    this.addMode = true;
    this.showData = true;
    this.syncParentSidebarWithAside();
    this.lockEdit = false;
    this.formTree = [];
    this.changedFields = [];
    this.formId = '';
    this.validationTouchKeys.clear();

    const prevActive = this.uniqForms.find(f => f.isActive);
    if (prevActive) prevActive.isActive = false;

    this.selectedForm = '';
    this.selectedFormSub = '';
    this.selectedParentElement = '';
    this.formSubOptions = [];
    this.parentElementNames = [];
    this.cdr.markForCheck();
  }

  private loadAddModeFilterOptions(): void {
    if (!this.selectedForm) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;

    this.selectedFormSub = '';
    this.selectedParentElement = '';
    this.loadingSubs = true;
    this.loadingParentElems = true;
    this.cdr.markForCheck();

    this.addEditService
      .loadFormSubOptions(baseUrl, this.selectedForm, this.submissionId, this.currentKeyId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.formSubOptions = data?.jsonObject || [];
          // Legacy parity (EfileElementsController.js L2550-L2552):
          //   if(vm.form_subs.length === 1) { vm.form_sub = vm.form_subs[0].FORM_SUB; }
          // Auto-select only when exactly one option exists.
          this.selectedFormSub =
            this.formSubOptions.length === 1
              ? this.formSubOptions[0].FORM_SUB || ''
              : '';
          this.loadingSubs = false;
          // CASCADE RE-EVALUATION: if parent-elements already loaded
          // (the HTTP race finished out of order) and we just locked in
          // a form-sub value, re-check whether dropdown 3 should now
          // auto-pick. Without this the user could see dropdown 2
          // populate AFTER dropdown 3 stayed empty when its own list
          // was length-1 but `selectedFormSub` was empty at the time
          // its response arrived — defeating the cascade gate.
          this.applyParentElementCascade();
          this.cdr.markForCheck();
        },
        (err: any) => {
          console.error('[EfileAddEdit] loadFormSubOptions failed', err);
          this.formSubOptions = [];
          this.loadingSubs = false;
          this.cdr.markForCheck();
        }
      );

    this.addEditService
      .loadParentElementNames(baseUrl, this.selectedForm, this.currentEntityTypeId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.parentElementNames = data?.jsonObject || [];
          // Legacy parity (EfileElementsController.js L2559-L2562):
          //   if(vm.parent_elems.length === 1) { vm.parent_element_name = ...; }
          // Auto-select only when exactly one option exists AND the
          // upstream form-sub dropdown already has a value (cascade
          // gate; see `applyParentElementCascade` and the rationale on
          // `rebuildFormFiltersForSelectedForm`).
          this.applyParentElementCascade();
          this.loadingParentElems = false;
          this.cdr.markForCheck();
        },
        (err: any) => {
          console.error('[EfileAddEdit] loadParentElementNames failed', err);
          this.parentElementNames = [];
          this.loadingParentElems = false;
          this.cdr.markForCheck();
        }
      );
  }

  /**
   * Cascade gate for dropdown 3 (`parent_element_name`).
   *
   * Add-mode loads form-subs and parent-elements in parallel HTTP calls
   * whose completion order is not guaranteed. The legacy `length === 1`
   * auto-pick must NOT fire while dropdown 2 (`form_sub`) is still
   * empty — otherwise the user sees dropdown 3 pre-filled while
   * dropdown 2 prompts for a choice, which is the inverted-cascade bug
   * the user flagged.
   *
   * Re-runs on every form-sub response AND every parent-elements
   * response so that whichever response arrives last picks up the
   * final value of `selectedFormSub`. Idempotent: only ASSIGNS when
   * `selectedParentElement` is currently empty, and only when both the
   * cascade gate (selectedFormSub set) and the length-1 guard hold.
   * Clears the value when the gate is broken (e.g. user re-changed the
   * form so form-sub list is now > 1 entry).
   */
  private applyParentElementCascade(): void {
    if (this.selectedFormSub && this.parentElementNames.length === 1) {
      this.selectedParentElement =
        this.parentElementNames[0]?.PARENT_ELEMENT_NAME || '';
    } else {
      this.selectedParentElement = '';
    }
  }

  loadFormData(): void {
    if (!this.selectedForm || !this.selectedFormSub || !this.selectedParentElement) return;
    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    this.loadingTree = true;
    this.changedFields = [];
    this.validationTouchKeys.clear();
    this.cdr.markForCheck();

    // buildHierarchyParams mirrors the _.merge(globalParams, filterParams, explicitParams)
    // that JsonObjectFactory.getJSONObj does in the legacy controller, including sso_id
    // (which getJSONObj always picks up from globalParams). Keep sso_id in the context —
    // the user's captured network payload confirms it must be present in the s3hr77 request.
    const s3hr77Context = this.buildHierarchyParams(this.getScenario());

    this.addEditService
      .loadFormData(
        baseUrl,
        this.selectedForm,
        this.submissionId,
        this.currentKeyId,
        this.selectedFormSub,
        this.selectedParentElement,
        '0',
        s3hr77Context,
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          const rawTree = data?.jsonObject;
          this.treeData = Array.isArray(rawTree) ? rawTree : rawTree ? [rawTree] : [];
          const formIds = _.compact(_.uniq(_.map(this.treeData, 'FORM_ID')));
          this.formId = formIds.length ? String(formIds[0]) : '';
          this.formTree = this.buildFormTree(this.treeData);
          // Legacy parity (controller L1987-L1993 / `vm.loadForm()` end of
          // success branch): the synthetic "New" tile is created AFTER the
          // s3hr77 request returns successfully, not before. Calling it
          // here (instead of the previous pre-request location) avoids
          // showing a red "New" tile when the user abandons or cancels
          // a form pick before the API even responds. Only add-mode picks
          // need the synthetic tile — edit-mode tile clicks already have
          // a real entry in `uniqForms`.
          if (this.addMode) {
            this.maybeFlagUnsavedForm();
          }
          this.loadingTree = false;
          this.cdr.markForCheck();
        },
        (err: any) => {
          console.error('[EfileAddEdit] loadFormData failed', err);
          this.loadingTree = false;
          this.cdr.markForCheck();
        }
      );
  }

  /**
   * Build a hierarchical tree from the flat s3hr77 rows.
   *
   * Direct port of AddEditController.loadForm (L2221-L2318). Each element row
   * has dotted ELEMENTNAME / TREEMAXOCCURS / ISREQUIRED. We walk segment-by-
   * segment and decide whether each element produces:
   *   - a leaf row (no repetition)
   *   - a header row (intermediate non-repeating segment)
   *   - a table row (intermediate segment with maxOccurs > 1)
   *   - additional cells in the previous table row's repeatedRows
   *
   * Deferred from the legacy port:
   *   - createPopupElement (nested repeating tables that open inner-grid).
   *     Falls back here to flat columns under the parent table.
   *   - lazyEfileLoadList virtualisation. The current tree is rendered fully.
   *     DECISION (parity audit): keep deferred. The single-scroll container
   *     + `trackBy` + `ChangeDetectionStrategy.OnPush` combo we use today
   *     keeps paint cost flat for the 100-300 repeated-row range we
   *     measured. Re-port via `cdk-virtual-scroll-viewport` ONLY if a real
   *     submission profile shows the outer-tree render exceeding ~16ms,
   *     or if a single table is observed with >500 rows.
   */
  private buildFormTree(rows: any[]): FormTreeRow[] {
    const tree: FormTreeRow[] = [];

    for (const element of rows || []) {
      const elementName: string = element?.ELEMENTNAME || '';
      if (!elementName) continue;

      const nodeNames = elementName.split(DOT);
      const maxOccurs = (element?.TREEMAXOCCURS || '').split(DOT);
      const isRequired = (element?.ISREQUIRED || '').split(DOT);

      for (let j = 0; j < nodeNames.length; j++) {
        const nodeName = nodeNames[j];
        const last = tree[tree.length - 1];
        const inRepeatingTable =
          tree.length > 1 &&
          last &&
          last.kind === 'table' &&
          elementName.includes(last.ELEMENTNAME) &&
          maxOccurs.some((m: string) => m && m !== '1');

        if (inRepeatingTable) {
          if (j !== nodeNames.length - 1) continue; // intermediates inside a table are folded into the cell key

          const sno = element.SNO ?? '';
          const parentLast = last as FormTreeRow;

          // ---- Nested-repeating detection (port of createPopupElement) ----
          //
          // Direct port of legacy EfileElementsController.loadForm L2028-L2048.
          // At the leaf of a path that runs through a repeating ancestor table,
          // three sub-branches exist; the popup column index is decided
          // SOLELY by `parentLast.ELEMENTNAME.includes(nodeNames[j-1])`:
          //
          //   (A) includesParentOfLeaf && scalarCase
          //       → flat scalar cell in outer grid (legacy L2028 branch).
          //   (B) includesParentOfLeaf && maxOccurs[j] !== '1'
          //       → legacy createPopupElement(j); the leaf itself is the
          //         repeating popup column (nestedAtIdx = j).
          //   (C) else (parent-of-leaf is a "different group" under the table)
          //       → legacy createPopupElement(j-1); the popup column is the
          //         PARENT segment, NOT some earlier ancestor (nestedAtIdx = j-1).
          //
          // The earlier implementation scanned for the FIRST repeating
          // ancestor between parentIdx+1..j and collapsed every leaf under
          // that ancestor into a single inner grid. For 3-level paths like
          // `OwnerLesseeUtlzFcltyLCAGrp.SupplierSectionDlvrUtlzGrp
          //   .SupplierMetricTonsDlvrUtlzGrp.DeliveredQty` (TREEMAXOCCURS
          // `1.n.n.1.1`) this folded `SupplierName` and
          // `SupplierMetricTonsDlvrUtlzGrp` siblings into one popup column
          // and dropped the per-group incremental affordance — which is
          // what the legacy `createPopupElement(j-1)` call explicitly
          // creates as SEPARATE popup columns per parent-of-leaf segment.
          const parentLastSegment = parentLast.ELEMENTNAME.split(DOT).pop() || '';
          const parentIdx = nodeNames.lastIndexOf(parentLastSegment);
          const includesParentOfLeaf =
            j >= 1 && !!nodeNames[j - 1] && parentLast.ELEMENTNAME.includes(nodeNames[j - 1]);
          const scalarCase = maxOccurs[j] === '1' || nodeNames.length === 2;

          let nestedAtIdx = -1;
          if (includesParentOfLeaf && scalarCase) {
            // Case (A): flat scalar — handled below in the non-nested leaf branch.
            nestedAtIdx = -1;
          } else if (includesParentOfLeaf) {
            // Case (B): legacy createPopupElement(j).
            nestedAtIdx = j;
          } else if (parentIdx >= 0 && j - 1 > parentIdx) {
            // Case (C): legacy createPopupElement(j-1).
            nestedAtIdx = j - 1;
          }

          if (nestedAtIdx !== -1) {
            // (1) Outer column for the nested cell — flagged isNested so the
            // template renders an "Edit (N)" button instead of an input.
            //
            // The outer-column header's `isRequired` flag drives the
            // yellow background (`.is-required` class) AND the red
            // asterisk on the `<th>` (template L443). Legacy
            // `createTableHeader` (EfileElementsController.js L2662)
            // uses `isRequired[nodeIndex] === 'REQ'` where `nodeIndex` is
            // the LEAF's index `j` — NOT the popup column's index.
            // This matters for case (C) "different group under
            // repeating ancestor" (`nestedAtIdx = j - 1`): the popup
            // column itself (e.g. `SupplierSectionDlvrUtlzGrp`) is
            // often OPT in the schema while a leaf under it
            // (e.g. `SupplierName`) is REQ — legacy renders the outer
            // column with the leaf's required state (yellow + asterisk),
            // and the previous migration that read `isRequired[nestedAtIdx]`
            // dropped that highlight on every nested-popup column built
            // via case (C). Use `isRequired[j]` to match legacy.
            const nestedColName = nodeNames[nestedAtIdx];
            const fullPathToNested = nodeNames.slice(0, nestedAtIdx + 1).join(DOT);
            this.upsertTableHeader(parentLast, nestedColName, element, isRequired[j] === 'REQ', true);

            // (2) Outer parent row — keyed by the FIRST SNO segment.
            const parentSno = String(sno).split(DOT)[0] || '0';
            const parentRow = this.upsertRepeatedRow(parentLast, parentSno, maxOccurs[parentIdx + 1] || maxOccurs[j], maxOccurs[parentIdx]);

            // (3) Outer cell — owns the nestedTable container.
            let cell = parentRow.cells[nestedColName];
            if (!cell || !cell.nestedTable) {
              cell = {
                ELEMENTNAME: nestedColName,
                FULLELEMENTNAME: fullPathToNested,
                ELEMENTVALUE: '',
                SNO: parentSno,
                isRequired: isRequired[nestedAtIdx] === 'REQ',
                nestedTable: {
                  parentSno,
                  parentColumn: nestedColName,
                  fullElementName: fullPathToNested,
                  tableHeaders: [],
                  repeatedRows: [],
                  repeatedRowsBySno: {},
                },
              };
              parentRow.cells[nestedColName] = cell;
            }
            const nested = cell.nestedTable!;

            // (4) Inner column for the LEAF nodeName.
            this.upsertNestedTableHeader(nested, nodeName, element, isRequired[j] === 'REQ');

            // (5) Inner row keyed by the leaf's full SNO. IS_REPEATED / MAX_OCCURS
            // must reflect the maxOccurs at the POPUP COLUMN's segment (legacy
            // createPopupElement L2634-L2635 uses `maxOccurs[j]` where its `j`
            // is the popup column index, i.e. our `nestedAtIdx`). Using the
            // leaf's maxOccurs here would always read `'1'` for paths like
            // `…SupplierSectionDlvrUtlzGrp.SupplierPersonNm` and hide the
            // incremental-add affordance on the inner grid even when the
            // popup column itself (`SupplierSectionDlvrUtlzGrp`, maxOccurs=`n`)
            // is the repeating segment.
            //
            // SNO NORMALISATION (legacy createPopupElement L2631-L2633):
            //   const SNOSplit  = element.SNO.split('.');
            //   const parentSNO = SNOSplit[0];
            //   const childSNO  = SNOSplit[SNOSplit.length - 1];
            //   const childFullSNO = parentSNO + '.' + childSNO;
            //   row.SNO = maxOccurs[j] != '1' ? childFullSNO : childSNO;
            //
            // For repeating popups (`maxOccurs[nestedAtIdx] != '1'`) the
            // inner-grid SNO column is rendered, and legacy normalises the
            // stored SNO to the `parentSNO.childSNO` dotted form so the
            // first row shows up as e.g. "0.0" — and `addRepeatedData`'s
            // `SNOParts.push(parseInt(SNOParts.pop()) + 1).join('.')` then
            // produces "0.1", "0.2" on subsequent additions. Without this
            // normalisation the backend may hand us a single-part SNO
            // (e.g. "0") when the leaf itself is max-1, which then made
            // `nextSno("0")` jump to "1" rather than "0.1". Use
            // `maxOccurs[nestedAtIdx]` (the popup column's maxOccurs) for
            // the decision instead of the leaf's — same rationale as
            // upsertNestedRow's `IS_REPEATED` derivation above — so that
            // three-level paths like `SupplierSectionDlvrUtlzGrp.<leaf>`
            // get the dotted SNO based on the repeating ancestor rather
            // than the always-max-1 leaf.
            const snoParts = String(sno).split(DOT);
            const parentSnoPart = snoParts[0] || '';
            const childSnoPart = snoParts[snoParts.length - 1] || '';
            const innerSno =
              maxOccurs[nestedAtIdx] !== '1'
                ? `${parentSnoPart}.${childSnoPart}`
                : childSnoPart;

            const innerRow = this.upsertNestedRow(nested, innerSno, maxOccurs[nestedAtIdx]);

            // (6) Inner cell value.
            // Prepend `" ;"` so the split produces a leading blank option
            // in the inner-grid `.scalar-field-input-wrap` select. Legacy
            // `createPopupElement` (EfileElementsController.js L2621) did
            // NOT add the prefix because AngularJS `ng-options` auto-injects
            // an unselected blank `<option>` when the model is unset —
            // Angular's `*ngFor` does not, so we must materialise the blank
            // entry here for parity with the outer-table cell behavior
            // (added at the call site below for `upsertLegacyTableHeader`
            // and the cell prefix at L1259).
            innerRow.cells[nodeName] = {
              ELEMENTNAME: nodeName,
              FULLELEMENTNAME: elementName,
              ELEMENTVALUE: element.ELEMENTVALUE,
              SNO: innerSno,
              MAXLENGTH: element.MAXLENGTH,
              ENUMVALUES: element.ENUMVALUES ? ` ;${element.ENUMVALUES}`.split(';') : undefined,
              isRequired: isRequired[j] === 'REQ',
              VALIDATIONPATTERN: element.VALIDATIONPATTERN,
              VALIDATIONEXPLANATION: element.VALIDATIONEXPLANATION,
            };
            continue;
          }

          // ---- Flat (non-nested) leaf in repeating table ----
          // (a) tableHeaders: append column if missing (legacy createTableHeader semantics)
          this.upsertLegacyTableHeader(parentLast, nodeName, element, nodeNames, maxOccurs, j, isRequired);

          // (b) repeatedRows: ensure SNO row exists
          const row = this.upsertRepeatedRow(parentLast, sno, maxOccurs[j], maxOccurs[j - 1]);

          // (c) cell value — outer-table cell `<select>` options come from
          // `cell.ENUMVALUES` directly (template `*ngFor="let opt of cell.ENUMVALUES"`),
          // so the legacy `" ;"` blank prefix must live on the CELL too —
          // not just the column header. Without this, cells materialised
          // during initial s3hr77 parsing (i.e. ones that already had data
          // when the form loaded) render the dropdown without a blank
          // entry, so the user can never re-select "blank" once a value
          // is in place. Mirrors `createTableHeader` legacy logic
          // (EfileElementsController.js L2647-L2694).
          row.cells[nodeName] = {
            ELEMENTNAME: nodeName,
            FULLELEMENTNAME: elementName,
            ELEMENTVALUE: element.ELEMENTVALUE,
            SNO: sno,
            MAXLENGTH: element.MAXLENGTH,
            ENUMVALUES: element.ENUMVALUES ? ` ;${element.ENUMVALUES}`.split(';') : undefined,
            isRequired: isRequired[j] === 'REQ',
            VALIDATIONPATTERN: element.VALIDATIONPATTERN,
            VALIDATIONEXPLANATION: element.VALIDATIONEXPLANATION,
          };
        } else if (j === nodeNames.length - 1) {
          // Leaf without repeating context — push as a normal element row.
          const enumValuesRaw = element.ENUMVALUES ? ` ;${element.ENUMVALUES}` : undefined;
          tree.push({
            kind: 'leaf',
            ELEMENTNAME: nodeName,
            FULLELEMENTNAME: elementName,
            level: j + 1,
            ELEMENTVALUE: element.ELEMENTVALUE,
            LINENO: element.LINENO,
            SNO: element.SNO,
            ENUMVALUES: enumValuesRaw ? enumValuesRaw.split(';') : undefined,
            MAXLENGTH: element.MAXLENGTH,
            VALIDATIONPATTERN: element.VALIDATIONPATTERN,
            VALIDATIONEXPLANATION: element.VALIDATIONEXPLANATION,
            isRequired: isRequired[j] === 'REQ',
            IS_EDITABLE: element.IS_EDITABLE,
            DESCRIPTION: element.DESCRIPTION,
            TYPESCHEMA: element.TYPESCHEMA,
            CHOICEGROUPID: element.CHOICEGROUPID,
            CHOICESEQ: element.CHOICESEQ,
          });
        } else {
          // Intermediate segment: emit a header row if not already present, OR
          // emit a new table row when the next segment opens up a repeating
          // group (legacy: "(maxOccurs[j+1] != '1' && !last.isHeader)").
          const exists = tree.findIndex(
            r => r.ELEMENTNAME === nodeName && r.level === j + 1
          ) !== -1;
          const opensTable =
            (maxOccurs[j + 1] != null && maxOccurs[j + 1] !== '1' && (!last || last.kind !== 'header'));

          if (!exists || opensTable) {
            const isTable =
              (maxOccurs[j] != null && maxOccurs[j] !== '1') ||
              (j + 1 === maxOccurs.length - 1 && maxOccurs[j + 1] !== '1');

            if (isTable) {
              tree.push({
                kind: 'table',
                ELEMENTNAME: nodeName,
                FULLELEMENTNAME: elementName,
                level: j + 1,
                tableHeaders: [],
                repeatedRows: [],
                repeatedRowsBySno: {},
                DESCRIPTION: element.DESCRIPTION,
                TYPESCHEMA: element.TYPESCHEMA,
                CHOICEGROUPID: element.CHOICEGROUPID,
                CHOICESEQ: element.CHOICESEQ,
              });
            } else {
              tree.push({
                kind: 'header',
                ELEMENTNAME: nodeName,
                FULLELEMENTNAME: elementName,
                level: j + 1,
                DESCRIPTION: element.DESCRIPTION,
                TYPESCHEMA: element.TYPESCHEMA,
                CHOICEGROUPID: element.CHOICEGROUPID,
                CHOICESEQ: element.CHOICESEQ,
              });
            }
          }
        }
      }
    }

    // Legacy parity (addEdit.html L634-L641): the AngularJS template ALWAYS
    // renders an input/select for every repeated-row column regardless of
    // whether `repData[nodeName]` actually exists in the API payload — typing
    // into an undefined model auto-creates the property. The migrated builder
    // only emits cells for elements the s3hr77 response shipped, so when a
    // row's data is sparse (e.g. 3 records at SNOs 0/21/27 where only SNO 0
    // ships values for every column) the template renders blank `<span>`
    // gaps instead of editable inputs. Mirror legacy by filling each row
    // with placeholder cells keyed by the column's nodeName so the editor
    // remains uniform across rows.
    this.fillPlaceholderCells(tree);

    return tree;
  }

  /**
   * Walk every repeated (`kind: 'table'`) row and every nested inner-grid
   * row, and insert an empty `RepeatedCell` for any header that doesn't yet
   * have a matching entry. Mirrors AngularJS's lazy `repData[nodeName]`
   * auto-vivification described above.
   *
   * Placeholder cells inherit metadata (ENUMVALUES, MAXLENGTH, validation,
   * isRequired, FULLELEMENTNAME) from the header so:
   *   - The select / text-input branch in the template picks the right
   *     control (`isEnumField` checks `cell.ENUMVALUES`).
   *   - `onFieldChange` records the correct dotted element name in
   *     `changedFields` (it falls back to `node.FULLELEMENTNAME`).
   *   - The validation hint resolves the same `${full}_${sno}` key used
   *     by populated cells.
   */
  private fillPlaceholderCells(tree: FormTreeRow[]): void {
    for (const row of tree) {
      if (row.kind !== 'table' || !row.repeatedRows || !row.tableHeaders) continue;
      for (const r of row.repeatedRows) {
        this.ensureRowHasCellsForHeaders(r, row.tableHeaders);
        // Recurse into nested inner-grid tables hosted on outer cells.
        for (const cellName of Object.keys(r.cells || {})) {
          const cell = r.cells[cellName];
          if (cell?.nestedTable) {
            for (const innerRow of cell.nestedTable.repeatedRows || []) {
              this.ensureRowHasCellsForHeaders(innerRow, cell.nestedTable.tableHeaders || []);
            }
          }
        }
      }
    }
  }

  private ensureRowHasCellsForHeaders(row: RepeatedRow, headers: TableHeader[]): void {
    if (!row || !headers) return;
    if (!row.cells) row.cells = {};
    const rowSno = String(row.SNO ?? '0');
    for (const h of headers) {
      if (!h?.nodeName) continue;
      const existing = row.cells[h.nodeName];
      if (existing) {
        existing.SNO = rowSno;
        continue;
      }
      row.cells[h.nodeName] = {
        ELEMENTNAME: h.nodeName,
        FULLELEMENTNAME: h.fullElementName || h.nodeName,
        ELEMENTVALUE: '',
        SNO: rowSno,
        MAXLENGTH: h.MAXLENGTH,
        ENUMVALUES: h.ENUMVALUES,
        isRequired: h.isRequired,
      };
    }
  }

  private upsertTableHeader(
    table: FormTreeRow, nodeName: string, element: any, isReq: boolean, isNested = false
  ): void {
    const headers = table.tableHeaders!;
    const existing = headers.findIndex(h => h.nodeName === nodeName);
    if (existing !== -1) {
      // Promote an existing flat column to nested if needed.
      if (isNested) headers[existing].isNested = true;
      return;
    }
    headers.push({
      nodeName,
      fullElementName: element.ELEMENTNAME,
      isRequired: isReq,
      // Legacy `createTableHeader` (EfileElementsController.js L2647-L2694)
      // prepended `" ;"` to ENUMVALUES so the split yielded a leading blank
      // option in outer-table cell `<select>`s. Restore that prefix here so
      // the dropdown starts in an unselected state to match legacy parity.
      ENUMVALUES: element.ENUMVALUES ? ` ;${element.ENUMVALUES}`.split(';') : undefined,
      MAXLENGTH: element.MAXLENGTH,
      isNested: isNested || undefined,
      DESCRIPTION: element.DESCRIPTION,
      TYPESCHEMA: element.TYPESCHEMA,
      CHOICEGROUPID: element.CHOICEGROUPID,
      CHOICESEQ: element.CHOICESEQ,
    });
  }

  /**
   * Port of legacy `createTableHeader` (EfileElementsController.js L2647-L2694).
   * This preserves `parentNodeName/selfEdit` metadata used by the table UI to
   * decide when to render input-only vs input+child-popup expander columns.
   */
  private upsertLegacyTableHeader(
    table: FormTreeRow,
    nodeName: string,
    element: any,
    nodeNames: string[],
    maxOccurs: string[],
    nodeIndex: number,
    isRequired: string[],
  ): void {
    const headers = table.tableHeaders || [];
    const existingSameNode = headers.findIndex(h => h.nodeName === nodeName);
    if (existingSameNode !== -1) {
      return;
    }

    let fullHeaderElementName = table.FULLELEMENTNAME || '';
    if (fullHeaderElementName) {
      const parts = fullHeaderElementName.split('.');
      while (parts.length && parts[parts.length - 1] !== table.ELEMENTNAME) {
        parts.pop();
      }
      fullHeaderElementName = parts.join('.');
    }

    const parentName = nodeNames[nodeIndex - 1];
    const tableIncludesParent = !!parentName && table.ELEMENTNAME.includes(parentName);
    const computedNodeName = tableIncludesParent ? nodeName : parentName;
    const parentNodeName = tableIncludesParent && (maxOccurs[nodeIndex] === '1' || nodeNames.length === 2)
      ? null
      : parentName;

    const hide = headers.filter(h => h.parentNodeName === parentName).length > 0;
    const header: TableHeader = {
      nodeName: computedNodeName || nodeName,
      fullElementName: '',
      isRequired: isRequired[nodeIndex] === 'REQ',
      // Legacy parity: prepend `" ;"` so the split produces a blank
      // leading option in outer-table repeated-cell `<select>`s
      // (EfileElementsController.js `createTableHeader` L2647-L2694).
      ENUMVALUES: element.ENUMVALUES ? ` ;${element.ENUMVALUES}`.split(';') : undefined,
      parentNodeName,
      hide,
      DESCRIPTION: element.DESCRIPTION,
      TYPESCHEMA: element.TYPESCHEMA,
      CHOICEGROUPID: element.CHOICEGROUPID,
      CHOICESEQ: element.CHOICESEQ,
      MAXLENGTH: element.MAXLENGTH,
    };

    const parentHeaderIndex = parentName
      ? headers.findIndex(h => h.nodeName === parentName)
      : -1;
    if (parentHeaderIndex === -1) {
      header.fullElementName = `${fullHeaderElementName}${header.parentNodeName ? `.${header.parentNodeName}` : ''}.${nodeName}`;
      headers.push(header);
      table.tableHeaders = headers;
      return;
    }

    // Legacy behavior: if parent header existed as a scalar column, convert
    // it into a popup host and keep editing enabled on that parent column.
    if (!headers[parentHeaderIndex].parentNodeName) {
      headers[parentHeaderIndex].parentNodeName = parentName;
      headers[parentHeaderIndex].selfEdit = true;
    }
  }

  private upsertRepeatedRow(
    table: FormTreeRow, sno: string, maxOccursAtJ: string, maxOccursAtParent: string
  ): RepeatedRow {
    const map = table.repeatedRowsBySno!;
    if (!map[sno]) {
      const row: RepeatedRow = {
        SNO: sno,
        MAX_OCCURS: maxOccursAtJ !== '1' ? maxOccursAtJ : maxOccursAtParent,
        cells: {},
        IS_REPEATED: maxOccursAtJ != null && String(maxOccursAtJ) !== '1',
      };
      map[sno] = row;
      table.repeatedRows!.push(row);
    }
    return map[sno];
  }

  /** Same as `upsertTableHeader` but on a NestedTable's headers list.
   *  Inner-grid headers carry a leading blank-option-bearing ENUMVALUES
   *  so placeholder cells materialised from headers (e.g. via
   *  `ensureRowHasCellsForHeaders` or `seedNestedRowFromHeaders`) render
   *  the same blank-option dropdown as the outer table — keeping
   *  `.scalar-field-input-wrap` selects consistent across both grids. */
  private upsertNestedTableHeader(
    nested: NestedTable, nodeName: string, element: any, isReq: boolean
  ): void {
    const headers = nested.tableHeaders;
    if (headers.findIndex(h => h.nodeName === nodeName) !== -1) return;
    headers.push({
      nodeName,
      fullElementName: element.ELEMENTNAME,
      isRequired: isReq,
      ENUMVALUES: element.ENUMVALUES ? ` ;${element.ENUMVALUES}`.split(';') : undefined,
      MAXLENGTH: element.MAXLENGTH,
      DESCRIPTION: element.DESCRIPTION,
      TYPESCHEMA: element.TYPESCHEMA,
      CHOICEGROUPID: element.CHOICEGROUPID,
      CHOICESEQ: element.CHOICESEQ,
    });
  }

  /** Same as `upsertRepeatedRow` but on a NestedTable's rows. */
  private upsertNestedRow(nested: NestedTable, sno: string, maxOccursAtJ: string): RepeatedRow {
    const map = nested.repeatedRowsBySno;
    if (!map[sno]) {
      const row: RepeatedRow = {
        SNO: sno,
        MAX_OCCURS: maxOccursAtJ,
        cells: {},
        IS_REPEATED: maxOccursAtJ != null && String(maxOccursAtJ) !== '1',
      };
      map[sno] = row;
      nested.repeatedRows.push(row);
    }
    return map[sno];
  }

  // ---------------------------------------------------------------------------
  // Cell edit + save — port of AddEditController.saveForm (L2556-L2635)
  // ---------------------------------------------------------------------------

  /**
   * Edit handler for both leaf rows and repeated-row cells. The legacy
   * `fieldEdited` (L2326) decoded ELEMENTNAME/SNO from `control.$name`, where
   * `$name` was set to `FULLELEMENTNAME + '_' + SNO` in the template, so
   * `split("_")[0]` always yielded the FULLELEMENTNAME (e.g.
   * "IRS5884.SecondYearWagesAmt"), NOT just the leaf ELEMENTNAME.
   * We must therefore use FULLELEMENTNAME as the primary source for
   * `elementName` in the change record so the rv8nq0 clob_data.changes payload
   * matches the legacy format.
   */
  /**
   * SNO written into `changedFields` / `clob_data.changes`.
   *
   * Legacy `fieldEdited` (EfileElementsController.js L2101-L2116) decodes
   * SNO from `control.$name` (`FULLELEMENTNAME + '_' + subRepHead['SNO']`).
   * Legacy parity (`fieldEdited` in EfileElementsController.js L2101-L2117):
   *   const SNO = control.$name.split("_")[1] || "0";
   * where the control name is `FULLELEMENTNAME + '_' + row.SNO` (see L2233).
   * So legacy ALWAYS keys changes by the row's own SNO — flat (`"0"`) on
   * the main form, dotted (`"0.0"`, `"0.1"`, …) inside the inner-grid
   * popup where `addOrOpenChildRow` (L2140-L2141) rewrites the seeded
   * inner row to `${repData.SNO}.${childIdx}`.
   *
   * The migrated seeding pipeline now guarantees inner rows carry the
   * full dotted SNO (see {@link seedNestedRowFromFirstSibling},
   * {@link applySnoToRow}, {@link snoForEmptyTable}, and the
   * `upsertNestedRow` `parentSNO.childSNO` normalisation in
   * `buildFormTree`), so a previous override that collapsed inner-row
   * SNOs back to the outer parent's first segment is no longer needed
   * — and was the cause of saves emitting `"sno":"0"` for the
   * incremental grouping-modal flow when `"sno":"0.0"` was expected.
   */
  private resolveChangeSno(row?: RepeatedRow | FormTreeRow | null): string {
    if (row) {
      return String(row.SNO ?? '0');
    }
    return '0';
  }

  /**
   * Legacy `fieldEdited` keys changes by `FULLELEMENTNAME + '_' + row.SNO`
   * (the repeated-row SNO), not the cell object's SNO. Host / nested cells
   * can still carry the outer parent segment (e.g. `"0"`) while the row is
   * dotted (`"0.0"`); always prefer the row SNO when recording edits.
   */
  onFieldChange(node: any, value: any, row?: RepeatedRow | null): void {
    const previous = node.ELEMENTVALUE ?? node.VALUE ?? '';
    if (String(previous) === String(value)) return;

    node.ELEMENTVALUE = value;
    node.VALUE = value;
    node.IS_CHANGED = 'Y';

    const sno = this.resolveChangeSno(row);

    const change = {
      elementName: node.FULLELEMENTNAME || node.element_name || node.ELEMENTNAME,
      sno,
      elementValue: value,
    };
    const idx = this.changedFields.findIndex(
      c => c.elementName === change.elementName && c.sno === change.sno
    );
    if (idx >= 0) this.changedFields[idx] = change;
    else this.changedFields.push(change);

    this.cdr.markForCheck();
  }

  onRepeatedCellChange(row: RepeatedRow, cell: RepeatedCell, value: any): void {
    if (!cell || !row) return;
    const rowSno = String(row.SNO ?? '0');
    cell.SNO = rowSno;
    this.onFieldChange(cell, value, row);
    row.cells[cell.ELEMENTNAME] = {
      ...cell,
      ELEMENTVALUE: value,
      IS_CHANGED: 'Y',
      SNO: rowSno,
    };
  }

  /**
   * Legacy inner-grid parity: edits are keyed by the header node name
   * (`subRepHead[repHeadBody.nodeName]`). Some migrated rows still resolve
   * via an alias key, so write through both the resolved cell and the
   * header-key slot to keep values visible after popup close/reopen.
   */
  onRepeatedCellChangeByHeader(row: RepeatedRow, header: TableHeader, value: any): void {
    if (!row || !header) return;
    if (!row.cells) row.cells = {};

    const rowSno = String(row.SNO ?? '0');
    const resolved = this.getRepeatedCell(row, header);
    const headerKey = header.nodeName;

    const cell: RepeatedCell = resolved || {
      ELEMENTNAME: headerKey,
      FULLELEMENTNAME: header.fullElementName || headerKey,
      ELEMENTVALUE: '',
      SNO: rowSno,
      MAXLENGTH: header.MAXLENGTH,
      ENUMVALUES: header.ENUMVALUES,
      isRequired: header.isRequired,
    };
    cell.SNO = rowSno;

    this.onFieldChange(cell, value, row);

    const updated: RepeatedCell = {
      ...cell,
      ELEMENTVALUE: value,
      IS_CHANGED: 'Y',
      SNO: rowSno,
    };
    row.cells[headerKey] = {
      ...updated,
      ELEMENTNAME: headerKey,
      FULLELEMENTNAME: header.fullElementName || updated.FULLELEMENTNAME,
    };

    if (resolved && resolved.ELEMENTNAME && resolved.ELEMENTNAME !== headerKey) {
      row.cells[resolved.ELEMENTNAME] = { ...updated };
    }
  }

  /**
   * Legacy table headers can represent a parent/group column while the runtime
   * value cell is keyed by a leaf element name. Resolve the usable cell for a
   * header so we don't render blank columns in migrated custom tables.
   */
  getRepeatedCell(row: RepeatedRow, header: TableHeader): RepeatedCell | null {
    if (!row?.cells || !header) {
      return null;
    }
    const direct = row.cells[header.nodeName];
    if (direct) {
      return direct;
    }

    const fullName = String(header.fullElementName || '');
    const leaf = fullName ? fullName.split('.').pop() || '' : '';
    if (leaf && row.cells[leaf]) {
      return row.cells[leaf];
    }

    return null;
  }

  /**
   * Show the external-link expander icon on a repeated-table cell.
   *
   * Legacy parity (addEdit.html L642 + add-edit-inner-grid.html L451):
   *   ng-if="::repHeadBody.parentNodeName"
   * — the decision is purely COLUMN-level. A cell-level flag must NOT
   * influence the render, otherwise two rows in the same column can
   * paint differently (one as expander, one as a scalar editor) when
   * only one of them happened to materialise a `nestedTable` during
   * lazy hydration. That asymmetry was the root cause of:
   *   - "some fields are incremental on row A but regular on row B"
   *     parity bug (rows differed by `cell.nestedTable` presence), and
   *   - "few groups don't show any fields" in the inner-grid (cells
   *     without a hydrated nestedTable were forced into the expander
   *     branch and never rendered an editor).
   *
   * Migration adds `h.isNested`: outer-grid columns that legacy marked
   * via `parentNodeName` are tagged with `isNested` when the column was
   * produced by the new `createPopupElement` port. Treat it as a
   * synonym for `parentNodeName` so the legacy behaviour is preserved
   * end-to-end. The `cell` parameter is retained only so the template
   * callers don't need to change signature; it is intentionally unused.
   */
  showRepeatedExpanderIcon(_cell: RepeatedCell | null, h: TableHeader): boolean {
    return !!(h.isNested || h.parentNodeName);
  }

  /**
   * Show the scalar input/select editor on a repeated-table cell.
   *
   * Legacy parity:
   *   - addEdit.html L635/L639 (outer grid): `(!parentNodeName || selfEdit)`
   *   - add-edit-inner-grid.html L455/L457: `!parentNodeName`
   *
   * Both decisions are COLUMN-only — `selfEdit` is the only outer-grid
   * branch that lets a column expose BOTH an inline editor AND the
   * expander, and legacy never had a `selfEdit` branch on the inner
   * grid. We honour `selfEdit` on both grids here for forward
   * compatibility, since legacy never had a column with `selfEdit:true`
   * inside an inner grid; for legacy data that flag is always falsy on
   * inner-grid columns so behaviour matches.
   *
   * Same parity rationale as `showRepeatedExpanderIcon`: the `cell`
   * argument is intentionally ignored so two rows of the same column
   * render identically regardless of which lazy paths fired earlier.
   */
  showRepeatedScalarEditor(_cell: RepeatedCell | null, h: TableHeader): boolean {
    if (h.isNested) return !!h.selfEdit;
    return !h.parentNodeName || !!h.selfEdit;
  }

  /**
   * Open nested popup using the resolved runtime cell key behind a header.
   * This keeps legacy parity when a header label differs from the actual
   * repeated-row cell key.
   *
   * For `isNested` columns the cell may exist as a placeholder (created by
   * fillPlaceholderCells) without a `nestedTable` yet — i.e. the API returned
   * no nested data for this outer row.  In that case we initialise the
   * nestedTable on-demand, borrowing column headers from another outer row that
   * already carries the same nested column's nestedTable, so the user can open
   * the inner-grid popup and add data.
   *
   * For legacy `createTableHeader` choice-group columns (`parentNodeName` on the
   * header, no `nestedTable` yet), lazily build `nestedTable` from a sibling row
   * or from `treeData` so the modal matches addOrOpenChildRow.
   *
   * @param parentTableRow  The parent FormTreeRow (kind='table') that owns
   *   `parentRow`.  Only needed when `header.isNested` and the cell has no
   *   nestedTable yet — used to borrow the inner tableHeaders from a sibling row.
   * @param siblingScanTable  When opening from the inner-grid modal, pass the
   *   current `NestedTable` so sibling scans use inner rows instead of outer table rows.
   */
  openNestedGridByHeader(
    parentRow: RepeatedRow,
    header: TableHeader,
    parentTableRow?: FormTreeRow,
    siblingScanTable?: RepeatedTableLike | null,
  ): void {
    const cell = this.getRepeatedCell(parentRow, header);
    const columnKey = header.nodeName;
    const siblingRows = (siblingScanTable?.repeatedRows ?? parentTableRow?.repeatedRows) || [];
    const hostCell = this.ensureHostCellForNestedColumn(parentRow, header, cell);

    // For isNested columns where the placeholder cell has no nestedTable yet,
    // initialise one so openNestedGrid can seed an empty row for editing.
    // Mirror legacy addOrOpenChildRow: borrow column headers from the first
    // sibling row that has data AND hydrate this row's actual data from
    // treeData (when an outer table context is available) so a row that wasn't
    // captured during buildFormTree still surfaces its real values instead of
    // only a synthetic seed row.
    if (header.isNested && hostCell && !hostCell.nestedTable) {
      this.lazyInitNestedTable(parentRow, header, hostCell, parentTableRow, siblingRows);
    }

    // Legacy choice-group / condition columns: `parentNodeName` on header, popup host.
    if (header.parentNodeName && hostCell && !hostCell.nestedTable) {
      this.tryLazyInitNestedTableForParentNodeName(
        parentRow,
        header,
        hostCell,
        parentTableRow,
        siblingScanTable,
      );
    }

    if (!hostCell?.nestedTable) {
      return;
    }

    // Legacy parity (`addOrOpenChildRow`, EfileElementsController.js L2159):
    //   repData[currentParentNode].tableHeaders = firstParentRow[currentParentNode].tableHeaders;
    // The popup ALWAYS refreshes its column list from the first sibling row
    // that has nested data, so every parent row's popup paints a consistent
    // column set even when this row's buildFormTree-built nestedTable only
    // captured a subset of columns. Without this refresh some SNOs opened a
    // partial / empty popup depending on which fragments the s3hr77 response
    // shipped for that specific parent row.
    this.refreshNestedTableHeadersFromSiblings(parentRow, hostCell.nestedTable, columnKey, siblingRows);

    // For rows whose hostCell.nestedTable was built by buildFormTree with an
    // empty tableHeaders (e.g. the API only returned one fragment for this
    // parent row), the refresh above can still leave headers empty when no
    // sibling has any either. Fall back to hydrating from treeData so the
    // popup shows the real column set rather than an empty body.
    //
    // Inner-on-inner case: when this opener was triggered from the
    // existing nested-grid popup, `parentTableRow` is undefined but
    // `siblingScanTable` carries the current nested context. Hydrate
    // from that scope too — without this, groups whose inner rows
    // were not captured by the outer-table buildFormTree showed an
    // empty modal body the first time the user expanded them.
    if (hostCell.nestedTable.tableHeaders.length === 0) {
      if (parentTableRow?.kind === 'table') {
        this.hydrateNestedTableFromTreeData(hostCell.nestedTable, parentTableRow, columnKey, parentRow);
      } else if (siblingScanTable && (siblingScanTable as NestedTable).fullElementName !== undefined) {
        this.hydrateNestedTableFromNestedScope(
          hostCell.nestedTable,
          siblingScanTable as NestedTable,
          columnKey,
          parentRow,
        );
      }
    }

    // Pass siblingRows so openNestedGrid can deep-clone the first sibling's
    // nested row as the seed (legacy `addOrOpenChildRow` L2129-L2156).
    this.openNestedGrid(parentRow, columnKey, siblingRows);
  }

  /**
   * Create a fresh nestedTable on `hostCell`, borrowing column headers from the
   * first sibling row that already carries one (so the popup paints the right
   * column set) and hydrating this row's actual treeData rows when an outer
   * table context is available (so existing data isn't replaced by a synthetic
   * seed).
   *
   * Mirrors the legacy `addOrOpenChildRow` flow which:
   *   1. Locates the first parent row that has data for this column.
   *   2. Copies its tableHeaders verbatim.
   *   3. Seeds a placeholder row when the current parent row has none of its
   *      own nested data.
   */
  private lazyInitNestedTable(
    parentRow: RepeatedRow,
    header: TableHeader,
    hostCell: RepeatedCell,
    parentTableRow: FormTreeRow | undefined,
    siblingRows: RepeatedRow[],
  ): void {
    const columnKey = header.nodeName;
    let templateHeaders: TableHeader[] = [];
    for (const otherRow of siblingRows) {
      if (otherRow === parentRow) continue;
      const otherCell = otherRow.cells?.[columnKey];
      if (otherCell?.nestedTable?.tableHeaders?.length) {
        templateHeaders = otherCell.nestedTable.tableHeaders;
        break;
      }
    }
    const tableScope =
      parentTableRow?.kind === 'table' ? this.tableScopeFullElementName(parentTableRow) : '';
    hostCell.nestedTable = {
      parentSno: String(parentRow.SNO ?? '').split(DOT)[0] || String(parentRow.SNO ?? '0'),
      parentColumn: columnKey,
      fullElementName:
        header.fullElementName || (tableScope ? `${tableScope}.${columnKey}` : columnKey),
      tableHeaders: templateHeaders.map(th => ({ ...th })),
      repeatedRows: [],
      repeatedRowsBySno: {},
    };

    if (parentTableRow?.kind === 'table') {
      this.hydrateNestedTableFromTreeData(hostCell.nestedTable, parentTableRow, columnKey, parentRow);
    }
  }

  /**
   * Bring an existing `nestedTable.tableHeaders` up to the most complete sibling
   * column list — direct port of legacy
   *   `repData[currentParentNode].tableHeaders = firstParentRow[currentParentNode].tableHeaders;`
   * (addOrOpenChildRow / EfileElementsController.js L2159). Without this, two
   * parent rows could open the same nested column and see DIFFERENT column sets
   * depending on which fragments the s3hr77 response shipped for each row.
   *
   * Only runs when a sibling's headers are a strict superset of this row's
   * (so we don't drop columns the current row already exposes). Headers are
   * cloned to keep references isolated.
   */
  private refreshNestedTableHeadersFromSiblings(
    parentRow: RepeatedRow,
    nested: NestedTable,
    columnKey: string,
    siblingRows: RepeatedRow[],
  ): void {
    let templateHeaders: TableHeader[] | null = null;
    for (const otherRow of siblingRows) {
      if (otherRow === parentRow) continue;
      const otherCell = otherRow.cells?.[columnKey];
      const candidate = otherCell?.nestedTable?.tableHeaders;
      if (candidate && candidate.length > 0) {
        if (!templateHeaders || candidate.length > templateHeaders.length) {
          templateHeaders = candidate;
        }
      }
    }
    if (!templateHeaders) return;

    const currentNames = new Set(nested.tableHeaders.map(h => h.nodeName));
    const siblingNames = new Set(templateHeaders.map(h => h.nodeName));

    // Only overwrite when sibling covers every column we already have AND adds
    // at least one new column. Otherwise we'd shadow the row's authentic header
    // metadata with a stale clone from a different row.
    const siblingCoversAll = [...currentNames].every(n => siblingNames.has(n));
    if (!siblingCoversAll) return;
    if (templateHeaders.length <= nested.tableHeaders.length) return;

    nested.tableHeaders = templateHeaders.map(th => ({ ...th }));
  }

  /**
   * Nested popup host is always keyed by `header.nodeName` in `parentRow.cells`
   * (legacy `repData[repHeadBody.nodeName]`).
   */
  private ensureHostCellForNestedColumn(
    parentRow: RepeatedRow,
    header: TableHeader,
    resolved: RepeatedCell | null,
  ): RepeatedCell | null {
    const key = header.nodeName;
    if (!parentRow.cells) {
      parentRow.cells = {};
    }
    let host = parentRow.cells[key];
    if (!host) {
      if (resolved) {
        host = { ...resolved, ELEMENTNAME: key };
      } else {
        host = {
          ELEMENTNAME: key,
          FULLELEMENTNAME: header.fullElementName || key,
          ELEMENTVALUE: '',
          SNO: parentRow.SNO,
          isRequired: header.isRequired,
          MAXLENGTH: header.MAXLENGTH,
          ENUMVALUES: header.ENUMVALUES,
        };
      }
      parentRow.cells[key] = host;
    }
    return host;
  }

  private tryLazyInitNestedTableForParentNodeName(
    parentRow: RepeatedRow,
    header: TableHeader,
    hostCell: RepeatedCell,
    parentTableRow?: FormTreeRow,
    siblingScanTable?: RepeatedTableLike | null,
  ): void {
    const columnKey = header.nodeName;
    const siblingRows = (siblingScanTable?.repeatedRows ?? parentTableRow?.repeatedRows) || [];

    // Borrow column headers from the first sibling row that has data (legacy
    // `firstParentRow[currentParentNode].tableHeaders`). When no sibling has
    // structure yet, we still need to create the nestedTable below so the
    // popup at least seeds an empty editable row.
    let templateHeaders: TableHeader[] = [];
    let borrowedFullElement = '';
    for (const otherRow of siblingRows) {
      if (otherRow === parentRow) continue;
      const otherHost = otherRow.cells?.[columnKey];
      const nt = otherHost?.nestedTable;
      if (nt?.tableHeaders?.length) {
        templateHeaders = nt.tableHeaders;
        borrowedFullElement = nt.fullElementName || '';
        break;
      }
    }

    const tableScope =
      parentTableRow?.kind === 'table' ? this.tableScopeFullElementName(parentTableRow) : '';
    hostCell.nestedTable = {
      parentSno: String(parentRow.SNO ?? '').split(DOT)[0] || String(parentRow.SNO ?? '0'),
      parentColumn: columnKey,
      fullElementName:
        borrowedFullElement ||
        header.fullElementName ||
        (tableScope ? `${tableScope}.${columnKey}` : columnKey),
      tableHeaders: templateHeaders.map(th => ({ ...th })),
      repeatedRows: [],
      repeatedRowsBySno: {},
    };

    // Always attempt to hydrate from treeData so the popup shows THIS row's
    // real values — not just the borrowed structure. Previously the function
    // returned early after borrowing headers, which caused popups for rows
    // that DID have data in treeData (but weren't captured by buildFormTree)
    // to render as just a synthetic seed row.
    if (parentTableRow?.kind === 'table') {
      this.hydrateNestedTableFromTreeData(hostCell.nestedTable, parentTableRow, columnKey, parentRow);
    }
  }

  /** Dotted path prefix for the active repeated table row (legacy FULLHEADERELEMENTNAME scope). */
  private tableScopeFullElementName(table: FormTreeRow): string {
    let full = table.FULLELEMENTNAME || '';
    if (full) {
      const parts = full.split(DOT);
      while (parts.length && parts[parts.length - 1] !== table.ELEMENTNAME) {
        parts.pop();
      }
      full = parts.join(DOT);
    }
    return full;
  }

  /**
   * True when `childSno` belongs to the outer repeated row (legacy dotted SNO under parent).
   */
  private isNestedSnoUnderOuterRow(childSno: string, outerRowSno: string): boolean {
    const c = String(childSno ?? '');
    const p = String(outerRowSno ?? '');
    if (!p.length) return true;
    return c === p || c.startsWith(`${p}.`);
  }

  /**
   * Populate inner `nestedTable` from flat `treeData` for a choice-group column
   * when no sibling had structure yet (mirrors createPopupElement + load).
   *
   * Two-tier matching:
   *   1. Exact `${tableScope}.${columnKey}.` prefix match (legacy default).
   *   2. Suffix fallback — when no exact match was found, accept any
   *      `treeData` row whose ELEMENTNAME path contains `.${columnKey}.`
   *      under the current parent SNO. This recovers nested headers for
   *      groups like `GDSNonRsdntlRealPropSpecify` where the s3hr77
   *      payload omits the outer table's full dotted prefix on inner
   *      element rows, leaving the modal with empty headers when only
   *      the strict prefix path was checked (legacy AngularJS swallowed
   *      this because `repData[parentNode].tableHeaders` was rebuilt
   *      from sibling rows on every open).
   */
  private hydrateNestedTableFromTreeData(
    nested: NestedTable,
    outerTable: FormTreeRow,
    columnKey: string,
    parentRow: RepeatedRow,
  ): void {
    const tableScope = this.tableScopeFullElementName(outerTable);
    const prefixBase = tableScope || outerTable.ELEMENTNAME || '';
    const prefix = `${prefixBase}.${columnKey}.`;
    const parentSno = String(parentRow.SNO ?? '');

    const matched = this.collectNestedTreeRows(prefix, columnKey, parentSno);
    this.applyNestedTreeRowsToTable(nested, matched);
  }

  /**
   * Hydrate a nested table when the call site has only the *current* nested
   * table context (no outer FormTreeRow). Used when the user opens the
   * inner-grid expander from inside another inner-grid popup, where
   * `openNestedGridByHeader` is invoked with `parentTableRow=undefined`.
   * Without this, suffix-only paths in `treeData` were dropped because
   * the legacy hydrator strictly required `parentTableRow?.kind === 'table'`.
   */
  private hydrateNestedTableFromNestedScope(
    nested: NestedTable,
    siblingScope: NestedTable,
    columnKey: string,
    parentRow: RepeatedRow,
  ): void {
    const scopeFull = siblingScope.fullElementName || siblingScope.parentColumn || '';
    const prefix = scopeFull ? `${scopeFull}.${columnKey}.` : `${columnKey}.`;
    const parentSno = String(parentRow.SNO ?? '');

    const matched = this.collectNestedTreeRows(prefix, columnKey, parentSno);
    this.applyNestedTreeRowsToTable(nested, matched);
  }

  /**
   * Suffix-aware tree scan used by both the outer-table and inner-table
   * hydration paths. Returns rows whose ELEMENTNAME either:
   *   - starts with `prefix` exactly (legacy strict path), OR
   *   - contains `.${columnKey}.` (suffix fallback for sparse payloads
   *     that omit the outer dotted prefix).
   *
   * The suffix fallback only runs when the strict pass returns nothing,
   * so existing strict-prefix payloads are untouched.
   */
  private collectNestedTreeRows(
    prefix: string,
    columnKey: string,
    parentSno: string,
  ): any[] {
    const tree = this.treeData || [];
    const strict: any[] = [];
    for (const el of tree) {
      const elementName: string = el?.ELEMENTNAME || '';
      if (!elementName.startsWith(prefix)) continue;
      const sno = String(el.SNO ?? '');
      if (!this.isNestedSnoUnderOuterRow(sno, parentSno)) continue;
      strict.push(el);
    }
    if (strict.length > 0) {
      return strict;
    }

    const suffix = `.${columnKey}.`;
    const fuzzy: any[] = [];
    for (const el of tree) {
      const elementName: string = el?.ELEMENTNAME || '';
      if (!elementName.includes(suffix)) continue;
      const sno = String(el.SNO ?? '');
      if (!this.isNestedSnoUnderOuterRow(sno, parentSno)) continue;
      fuzzy.push(el);
    }
    return fuzzy;
  }

  /**
   * Apply collected `treeData` rows onto `nested.tableHeaders` /
   * `nested.repeatedRows`. Extracted from
   * {@link hydrateNestedTableFromTreeData} so both the strict and the
   * nested-scope hydrator share the same upsert logic.
   */
  private applyNestedTreeRowsToTable(nested: NestedTable, matchedRows: any[]): void {
    const parentOuterSno = String(nested.parentSno ?? '').split(DOT)[0] || '0';
    for (const el of matchedRows) {
      const elementName: string = el?.ELEMENTNAME || '';
      if (!elementName) continue;
      const rawSno = String(el.SNO ?? '');
      const nodeNames = elementName.split(DOT);
      const maxOccurs = (el?.TREEMAXOCCURS || '').split(DOT);
      const isReq = (el?.ISREQUIRED || '').split(DOT);
      const j = nodeNames.length - 1;
      const leafName = nodeNames[j];
      // Same normalisation as `buildFormTree` / `createPopupElement`
      // (EfileElementsController.js L2595-L2633): inner rows are keyed
      // under the outer parent index, not the bare child segment from the
      // API (`"0"` on outer row `"1"` → `"1.0"` when repeating).
      const snoParts = rawSno.split(DOT);
      const childSnoPart = snoParts[snoParts.length - 1] || '';
      const sno =
        maxOccurs[j] !== '1'
          ? `${parentOuterSno}.${childSnoPart}`
          : childSnoPart;
      this.upsertNestedTableHeader(nested, leafName, el, isReq[j] === 'REQ');
      const innerRow = this.upsertNestedRow(nested, sno, maxOccurs[j]);
      // Prepend `" ;"` to ENUMVALUES so inner-grid scalar selects render
      // the leading blank option, matching the outer-table behavior and
      // the createPopupElement migration site at L1234-1241.
      innerRow.cells[leafName] = {
        ELEMENTNAME: leafName,
        FULLELEMENTNAME: elementName,
        ELEMENTVALUE: el.ELEMENTVALUE,
        SNO: sno,
        MAXLENGTH: el.MAXLENGTH,
        ENUMVALUES: el.ENUMVALUES ? ` ;${el.ENUMVALUES}`.split(';') : undefined,
        isRequired: isReq[j] === 'REQ',
        VALIDATIONPATTERN: el.VALIDATIONPATTERN,
        VALIDATIONEXPLANATION: el.VALIDATIONEXPLANATION,
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Validation UI — port of addEdit.html `ng-pattern` + `$invalid && $touched`
  // + `uib-tooltip` on `fa-info-circle` (L307–L316, L603, L633).
  // ---------------------------------------------------------------------------

  private validationTouchKey(fullElementName: string, sno: string): string {
    return `${fullElementName}_${sno}`;
  }

  onLeafValidationBlur(row: FormTreeRow): void {
    if (row.kind !== 'leaf') {
      return;
    }
    this.validationTouchKeys.add(this.validationTouchKey(row.FULLELEMENTNAME, String(row.SNO ?? '0')));
    this.cdr.markForCheck();
  }

  onRepeatedValidationBlur(r: RepeatedRow, cell: RepeatedCell): void {
    this.validationTouchKeys.add(this.validationTouchKey(cell.FULLELEMENTNAME, String(r.SNO ?? '0')));
    this.cdr.markForCheck();
  }

  /**
   * Mirrors AngularJS `ng-pattern` semantics (addEdit.html L597/L638):
   *   - No pattern  → always valid.
   *   - Empty value → always valid (AngularJS calls `ctrl.$isEmpty(value)` and
   *     skips the regex when the field is empty, matching the HTML5 `pattern`
   *     attribute spec: "constraint is only satisfied if the value is not the
   *     empty string").  Returning false here prevents empty new rows / unfilled
   *     optional fields from triggering "Please fix the data errors" on save.
   *   - Non-empty value that fails the pattern → invalid.
   */
  isPatternInvalid(value: any, patternStr?: string | null): boolean {
    const p = patternStr != null ? String(patternStr).trim() : '';
    if (!p) {
      return false;
    }
    const s = value == null ? '' : String(value);
    if (!s) {
      return false; // empty value is always valid, matching AngularJS ng-pattern
    }
    try {
      return !new RegExp(p).test(s);
    } catch {
      return false;
    }
  }

  showLeafValidationHint(row: FormTreeRow): boolean {
    if (row.kind !== 'leaf') {
      return false;
    }
    const key = this.validationTouchKey(row.FULLELEMENTNAME, String(row.SNO ?? '0'));
    return this.validationTouchKeys.has(key) && this.isPatternInvalid(row.ELEMENTVALUE, row.VALIDATIONPATTERN);
  }

  showRepeatedValidationHint(r: RepeatedRow, cell: RepeatedCell): boolean {
    const key = this.validationTouchKey(cell.FULLELEMENTNAME, String(r.SNO ?? '0'));
    return this.validationTouchKeys.has(key) && this.isPatternInvalid(cell.ELEMENTVALUE, cell.VALIDATIONPATTERN);
  }

  /**
   * Pre-save validity gate — port of `askComment`'s `addEditForm.$invalid`
   * block (EfileElementsController.js L2271-L2280). Walks the formTree
   * including every nested inner-grid, marks every invalid cell as touched
   * (so the red `fa-info-circle` hint shows up just like AngularJS's
   * `$setTouched()`), and returns true if any field is invalid.
   *
   * "Invalid" follows the legacy `ng-pattern` semantics implemented by
   * {@link isPatternInvalid}: only fields with a non-empty
   * `VALIDATIONPATTERN` whose value fails to match are reported.
   */
  private markInvalidFieldsTouched(): boolean {
    let hasInvalid = false;

    const checkRepeatedRow = (row: RepeatedRow): void => {
      if (row.toDelete) return;
      const sno = String(row.SNO ?? '0');
      Object.keys(row.cells || {}).forEach(col => {
        const cell = row.cells[col];
        if (!cell) return;
        if (cell.nestedTable) {
          (cell.nestedTable.repeatedRows || []).forEach(checkRepeatedRow);
          return;
        }
        if (this.isPatternInvalid(cell.ELEMENTVALUE, cell.VALIDATIONPATTERN)) {
          this.validationTouchKeys.add(this.validationTouchKey(cell.FULLELEMENTNAME, sno));
          hasInvalid = true;
        }
      });
    };

    (this.formTree || []).forEach(row => {
      if (row.kind === 'leaf') {
        if (this.isPatternInvalid(row.ELEMENTVALUE, row.VALIDATIONPATTERN)) {
          this.validationTouchKeys.add(this.validationTouchKey(row.FULLELEMENTNAME, String(row.SNO ?? '0')));
          hasInvalid = true;
        }
      } else if (row.kind === 'table') {
        (row.repeatedRows || []).forEach(checkRepeatedRow);
      }
    });

    if (hasInvalid) {
      this.cdr.markForCheck();
    }
    return hasInvalid;
  }

  // ---------------------------------------------------------------------------
  // Add / delete repeated rows  — port of AddEditController.addRepeatedData
  // (L2641) and AddEditController.deleteRow (L2453).
  // ---------------------------------------------------------------------------

  /**
   * Append a new repeated row to a `kind: 'table'` formTree row OR to a
   * `NestedTable` (inner-grid popup). Clones the last non-deleted row as
   * a template, increments the dotted SNO, and clears each cell's
   * ELEMENTVALUE. Empty new rows are NOT pushed into `changedFields` — the
   * legacy code only tracks fields that go `$dirty`, and an untouched new
   * row stays out of the save payload.
   */
  addRepeatedRow(table: RepeatedTableLike): void {
    if (!table?.repeatedRows) return;
    if (this.isProductionAccepted || this.lockEdit || this.isSubmissionLocked) return;

    const nonDeleted = table.repeatedRows.filter(r => !r.toDelete);
    const seed = nonDeleted.length > 0
      ? nonDeleted[nonDeleted.length - 1]
      : table.repeatedRows[0];
    if (!seed) return;

    // MAX_OCCURS enforcement — legacy `addRepeatedData` had this guard
    // commented out (EfileElementsController.js L2395-L2397), which let
    // users add rows past the schema cap. Migration enforces the cap so
    // the UI honours the "Max records allowed: N" label that the inner
    // grid already shows next to the table. `"n"` (and `"unbounded"`)
    // mean unlimited per schema convention; anything else parses to a
    // positive integer.
    const cap = this.resolveMaxOccursCap(seed.MAX_OCCURS ?? table.repeatedRows[0]?.MAX_OCCURS);
    if (cap !== null && nonDeleted.length >= cap) {
      this.gtwSnackbarService.openSnackBar(
        `Maximum ${cap} record${cap === 1 ? '' : 's'} allowed.`,
        'gtw-snackbar--yellow'
      );
      return;
    }

    // Grouped-SNO preservation (parity with legacy add-after-delete):
    // when every visible row has been soft-deleted, the previous code
    // collapsed the new SNO down to plain `'0'`, which dropped the
    // grouping prefix (e.g. `0.0`/`0.1` reverted to a flat `0`). Derive
    // the dotted shape from the seed SNO so grouped rows continue with
    // the same parent prefix even after a full delete-then-add cycle.
    const newSno = nonDeleted.length === 0
      ? this.snoForEmptyTable(seed.SNO, table.repeatedRows)
      : this.nextSno(seed.SNO);
    const newCells: Record<string, RepeatedCell> = {};
    Object.keys(seed.cells).forEach(name => {
      const src = seed.cells[name];
      if (!src) return;
      // Legacy parity (controller L2406):
      //   if (newRow[el].isTable) newRow[el] = undefined;
      // The seed's nested-table cells are explicitly NOT cloned onto the
      // new row. The user can re-open the inner-grid popup on the new
      // row to seed a fresh empty nested table; without this guard the
      // new row inherited the seed's nested children verbatim, which
      // was a parity regression flagged in the audit.
      if (src.nestedTable) {
        return;
      }
      newCells[name] = {
        ELEMENTNAME: src.ELEMENTNAME,
        FULLELEMENTNAME: src.FULLELEMENTNAME,
        ELEMENTVALUE: '',
        SNO: newSno,
        MAXLENGTH: src.MAXLENGTH,
        ENUMVALUES: src.ENUMVALUES,
        isRequired: src.isRequired,
        VALIDATIONPATTERN: src.VALIDATIONPATTERN,
        VALIDATIONEXPLANATION: src.VALIDATIONEXPLANATION,
      };
    });
    const newRow: RepeatedRow = {
      SNO: newSno,
      MAX_OCCURS: seed.MAX_OCCURS,
      cells: newCells,
      isNew: true,
      IS_REPEATED: seed.IS_REPEATED,
    };
    // Mirror legacy parity (addEdit.html L634-L641 — see fillPlaceholderCells):
    // even if the seed row was sparse, the newly-added row must expose an
    // editable input for EVERY header column. Without this, columns whose
    // cell was missing on the seed stay missing on the new row too.
    if (table.tableHeaders) {
      this.ensureRowHasCellsForHeaders(newRow, table.tableHeaders);
    }
    table.repeatedRows.push(newRow);
    if (table.repeatedRowsBySno) table.repeatedRowsBySno[newSno] = newRow;
    this.cdr.markForCheck();
  }

  /**
   * Parse a row's `MAX_OCCURS` into a positive integer cap, or `null`
   * for unlimited (`'n'` / `'unbounded'` / missing / non-positive).
   * Used by {@link addRepeatedRow} and {@link canAddRepeatedRow}.
   */
  private resolveMaxOccursCap(raw: string | undefined): number | null {
    if (raw == null) return null;
    const v = String(raw).trim();
    if (!v || v === 'n' || v.toLowerCase() === 'unbounded') return null;
    const n = parseInt(v, 10);
    if (isNaN(n) || n <= 0) return null;
    return n;
  }

  /**
   * Template helper — `true` when another row can still be added to the
   * given repeating table. Drives the `+` add-row icon's disabled
   * state on both the outer-grid title row and the inner-grid SNO
   * header (parity with the MAX_OCCURS cap enforced inside
   * {@link addRepeatedRow}).
   */
  canAddRepeatedRow(table: RepeatedTableLike | null | undefined): boolean {
    if (!table?.repeatedRows) return false;
    if (this.isReadOnly) return false;
    const nonDeletedCount = table.repeatedRows.filter(r => r && !r.toDelete).length;
    const seed = table.repeatedRows.find(r => r && !r.toDelete) || table.repeatedRows[0];
    const cap = this.resolveMaxOccursCap(seed?.MAX_OCCURS);
    if (cap === null) return true;
    return nonDeletedCount < cap;
  }

  /**
   * Soft-delete a repeated row. Fires an empty-value change for every cell
   * so the save payload removes them server-side (matches legacy
   * deleteRow + fieldEdited round-trip in vm.changes).
   *
   * Works on both top-level tables (`FormTreeRow` kind=table) and on
   * `NestedTable` rows inside an inner-grid popup.
   *
   * Legacy parity (controller L2228-L2253): `deleteRow` is ONE-WAY.
   * Clicking the `fa-minus-square` icon marks the row deleted, pushes
   * empty-value changes for every cell at every nesting level, and the
   * row immediately disappears from `visibleRepeatedRows`. There is no
   * "undelete" affordance in legacy and the migrated UI never surfaced
   * one either (the row vanishes the instant `toDelete=true`), so the
   * earlier toggle variant's undo branch was unreachable dead code.
   *
   * Recursive descent (legacy L2236-L2240): when a row has nested-table
   * cells, walk EVERY descendant row and queue empty-value changes for
   * each leaf cell so the entire subtree is dropped from the save
   * payload — not just the row's own scalar cells. Without this, nested
   * children would be orphaned in the rv8nq0 payload.
   *
   * Method name kept as `toggleDeleteRow` to minimise template/test
   * churn even though it now performs a one-way delete.
   */
  toggleDeleteRow(_table: RepeatedTableLike, row: RepeatedRow): void {
    if (!row) return;
    if (this.isProductionAccepted || this.lockEdit || this.isSubmissionLocked) return;
    if (row.toDelete) return; // legacy one-way: already deleted, do nothing

    row.toDelete = true;
    this.applyDeleteRecursive(row);
    this.cdr.markForCheck();
  }

  /**
   * Walk `row` (and any `cell.nestedTable.repeatedRows` it owns) marking
   * each scalar cell as deleted in `changedFields`. Used by
   * {@link toggleDeleteRow}; mirrors the recursive isTable check at
   * `EfileElementsController.js` L2236-L2240.
   */
  private applyDeleteRecursive(row: RepeatedRow): void {
    if (!row || !row.cells) return;
    Object.keys(row.cells).forEach(name => {
      const cell = row.cells[name];
      if (!cell) return;

      // Recurse into nested-table cells before flagging the cell itself
      // so legacy behaviour is preserved: parents fire AFTER their
      // children, but each cell at every level pushes its own empty-
      // value change exactly once.
      if (cell.nestedTable?.repeatedRows?.length) {
        cell.nestedTable.repeatedRows.forEach(child => {
          if (child.toDelete) return;
          child.toDelete = true;
          this.applyDeleteRecursive(child);
        });
      }

      const change = {
        elementName: cell.FULLELEMENTNAME || cell.ELEMENTNAME,
        sno: this.resolveChangeSno(row),
        elementValue: '',
      };
      const idx = this.changedFields.findIndex(
        c => c.elementName === change.elementName && c.sno === change.sno
      );
      if (idx >= 0) this.changedFields[idx] = change;
      else this.changedFields.push(change);
      cell.ELEMENTVALUE = '';
      cell.IS_CHANGED = 'Y';
    });
  }

  /**
   * Increment the last dotted segment of an SNO (e.g. "1" → "2", "1.3" → "1.4").
   * Mirrors the legacy SNOParts.push((parseInt(SNOParts.pop()) + 1)) idiom.
   */
  private nextSno(prev: string): string {
    if (!prev) return '0';
    const parts = String(prev).split('.');
    const tail = parseInt(parts.pop() || '0', 10);
    parts.push(String((isNaN(tail) ? 0 : tail) + 1));
    return parts.join('.');
  }

  /**
   * Compute the SNO to use when every visible row has been soft-deleted
   * and the user adds a new row.
   *
   * Two-tier behaviour:
   *   1. If the seed (or any historical row) carries a dotted SNO
   *      (e.g. `0.1`), preserve the grouping prefix but RESET the tail
   *      back to `0` so adds restart at `{prefix}.0`. The user requested
   *      this explicitly: after deleting every row in a grouping
   *      overlay, reopening the modal should show the `0.0` entry and
   *      subsequent adds should continue as `0.1`, `0.2`, ... — not
   *      pick up from `lastDeletedTail + 1`.
   *   2. Flat tables (single-segment SNOs) reset to `'0'` — the
   *      grouping prefix never existed there to preserve.
   */
  private snoForEmptyTable(seedSno: string | undefined, allRows: RepeatedRow[]): string {
    const allSnos: string[] = [];
    if (seedSno) allSnos.push(String(seedSno));
    for (const r of allRows || []) {
      if (r?.SNO != null) allSnos.push(String(r.SNO));
    }
    for (const sno of allSnos) {
      const parts = sno.split('.');
      if (parts.length < 2) continue;
      const head = parts.slice(0, -1).join('.');
      if (head) {
        return `${head}.0`;
      }
    }
    return '0';
  }

  /**
   * Rewrite a row's SNO and propagate the change down to every cell so
   * `cell.SNO` stays consistent with `row.SNO`. Used by
   * {@link openNestedGrid} when seeding a fresh starter row after a
   * delete-all-then-reopen cycle: without the cell-level rewrite, save
   * payload changes would still reference the seed donor's original SNO.
   */
  private applySnoToRow(row: RepeatedRow, sno: string): void {
    row.SNO = sno;
    if (row.cells) {
      Object.keys(row.cells).forEach(k => {
        const c = row.cells[k];
        if (c) c.SNO = sno;
      });
    }
  }

  /**
   * Copy `IS_REPEATED` (true) and `MAX_OCCURS` from any existing row in
   * `nested.repeatedRows` (deleted or not) onto `seedRow` so a fresh
   * starter row produced after a delete-all cycle keeps the original
   * grouping table's repeating semantics.
   *
   * Why both flags matter:
   *   - `IS_REPEATED` gates `nestedTableShowSnoAddColumn` (template
   *     L632 / L653) — the SNO header column and per-row delete icon
   *     only render when the LAST row's `IS_REPEATED` is truthy. If
   *     the seed has it as false (which `seedNestedRowFromHeaders`
   *     hardcodes), the modal looks like a non-repeating "plain"
   *     table and the user can't add or delete grouped rows.
   *   - `MAX_OCCURS` powers {@link canAddRepeatedRow} / the
   *     "Max records allowed" hint. Carrying it forward keeps the cap
   *     enforcement consistent across delete-all + add cycles.
   *
   * Does NOT downgrade an already-true `IS_REPEATED` on the seed
   * (`seedNestedRowFromFirstSibling` may already have set it from
   * the donor's MAX_OCCURS), and only fills `MAX_OCCURS` when the
   * seed doesn't already carry one.
   */
  private inheritRepeatingFlagsFromExistingRows(
    seedRow: RepeatedRow,
    nested: NestedTable,
  ): void {
    for (const r of nested.repeatedRows || []) {
      if (!r) continue;
      if (r.IS_REPEATED && !seedRow.IS_REPEATED) {
        seedRow.IS_REPEATED = true;
      }
      if (
        r.MAX_OCCURS != null &&
        r.MAX_OCCURS !== '' &&
        (seedRow.MAX_OCCURS == null || seedRow.MAX_OCCURS === '')
      ) {
        seedRow.MAX_OCCURS = r.MAX_OCCURS;
      }
    }
  }

  /**
   * Legacy `ng-show="!repData.toDelete && repData['SNO']"` (addEdit.html L630)
   * — rows marked deleted disappear, and rows with a falsy SNO are skipped
   * entirely. The SNO check guards against sparse rows that get materialised
   * by lazy nested-host creation (e.g. parent-column expansion before the
   * row is fully seeded). There is no in-grid restore (`fa-undo`) in legacy.
   */
  visibleRepeatedRows(row: FormTreeRow): RepeatedRow[] {
    return (row.repeatedRows || []).filter(r => !r.toDelete && !!r.SNO);
  }

  /**
   * Legacy `add-edit-inner-grid.html` L444 / L673 — same sparse-SNO guard
   * as `visibleRepeatedRows` (`ng-show="!subRepHead.toDelete && subRepHead['SNO']"`).
   */
  visibleNestedRows(nested: NestedTable): RepeatedRow[] {
    return (nested.repeatedRows || []).filter(r => !r.toDelete && !!r.SNO);
  }

  /**
   * Direct port of legacy `addEdit.html` L606-L608 dynamic table height:
   *
   *   style="height: {{dataRow.repeatedData.length>5 ? 200+'px' : (height+75)+'px'}}"
   *   where  height = (localRepeatedData.length) * 20
   *
   * Returns the px value to set as `max-height` on the `.form-tree-table`
   * wrapper so the table itself becomes a scrollable region (sticky thead
   * already in SCSS) for tables with more than 5 visible rows. Smaller
   * tables keep the legacy "content-fit + 75px footer slack" sizing.
   *
   * NOTE: Legacy also wired in a windowed `vr-scroll-to`/`lazyEfileLoadList`
   * to render only ~10 rows at a time (controller L2183-L2227 + `tableScroll`
   * listener). We render every row inside this scroll container instead,
   * relying on `trackBy` + OnPush change detection to keep paint cost
   * down. For the 100-300 repeated-row range we measured this performs
   * comparably to the legacy windowing.
   *
   * DECISION (parity audit): keep this approach. Re-port `vr-scroll-to`
   * (or swap in `cdk-virtual-scroll-viewport`) ONLY when a real submission
   * profile shows the outer-tree paint exceeding one frame or when a
   * single repeated table is observed with >500 rows. The 200px clamp
   * here is enough for the legacy use cases.
   */
  repeatedTableMaxHeightPx(row: FormTreeRow | NestedTable | null | undefined): number | undefined {
    const rows = (row?.repeatedRows || []).filter(r => !r?.toDelete);
    const count = rows.length;
    // Only cap height (and thus trigger overflow scroll) when there are more
    // than 5 rows. For small tables the div grows to fit content naturally,
    // which prevents a spurious vertical scrollbar from appearing when the
    // actual rendered row height (input-sm ~30px + td padding ~10px = ~40px)
    // exceeds the legacy formula's 20px-per-row estimate.
    return count > 5 ? 200 : undefined;
  }

  /** Legacy AddEditController.removeQoutes (EfileElementsController.js L2386). */
  removeQuotes(desc: any): string {
    return desc != null ? String(desc).replace(/"/g, '') : '';
  }

  /**
   * Multi-line tooltip string for element / column-header hovers — port
   * of the legacy `uib-tooltip-html` markup at `addEdit.html` L585-L590
   * (element-name) and L617-L623 (column header) reduced to a single
   * `\n`-separated string that the browser's native `title` attribute
   * renders as multi-line text.
   *
   * Rows with empty/missing values are dropped so the tooltip stays
   * compact for fields that don't carry DESCRIPTION / TYPESCHEMA /
   * CHOICEGROUPID / CHOICESEQ (legacy showed empty rows too, but the
   * browser-native tooltip looks cleaner without them).
   *
   * Returns `null` when no data is available so the template's
   * `[attr.title]` binding suppresses the tooltip entirely instead of
   * rendering an empty hover bubble.
   */
  schemaTooltipText(model: any): string | null {
    if (!model) return null;
    const name = model.ELEMENTNAME ?? model.nodeName ?? '';
    const rows: Array<[string, string]> = [
      ['Name:', String(name)],
      ['DESCRIPTION:', this.removeQuotes(model.DESCRIPTION)],
      ['Type:', model.TYPESCHEMA != null ? String(model.TYPESCHEMA) : ''],
      ['Choice Group Id:', model.CHOICEGROUPID != null ? String(model.CHOICEGROUPID) : ''],
      ['Choice Seq:', model.CHOICESEQ != null ? String(model.CHOICESEQ) : ''],
    ];
    const lines = rows
      .filter(([, v]) => v != null && String(v).trim() !== '')
      .map(([k, v]) => `${k} ${v}`);
    return lines.length ? lines.join('\n') : null;
  }

  /** Legacy line cell `uib-tooltip` when LINENO is non-blank (addEdit.html L580). */
  lineColumnTooltip(row: FormTreeRow): string | null {
    const v = row.LINENO;
    return v != null && String(v).trim() !== '' ? String(v) : null;
  }

  /** Legacy add-edit-inner-grid.html L442 — SNO header + add only when last row is repeated. */
  nestedTableShowSnoAddColumn(nested: NestedTable): boolean {
    const rows = nested?.repeatedRows || [];
    if (!rows.length) {
      return false;
    }
    return !!rows[rows.length - 1].IS_REPEATED;
  }

  /** Legacy add-edit-inner-grid.html L437 — label when MAX_OCCURS is greater than 1. */
  nestedMaxOccursDisplay(nested: NestedTable): string | null {
    const m = nested?.repeatedRows?.[0]?.MAX_OCCURS;
    if (m == null || m === '') {
      return null;
    }
    const n = parseInt(String(m), 10);
    if (!isNaN(n) && n > 1) {
      return String(m);
    }
    return null;
  }

  /** Legacy footer line (addEdit.html L657) using visible slice + total `repeatedData.length`. */
  repeatedTablePaginationText(row: FormTreeRow): string {
    const visible = this.visibleRepeatedRows(row);
    const total = row.repeatedRows?.length ?? 0;
    if (!visible.length || !total) {
      return '';
    }
    const tailNum = (s: string) => parseInt(String(s).split('.').pop() || '0', 10);
    const from = tailNum(visible[0].SNO) + 1;
    const to = tailNum(visible[visible.length - 1].SNO) + 1;
    return `Showing ${from} to ${to} of ${total}`;
  }

  // ---------------------------------------------------------------------------
  // Inner-grid popup — port of AddEditController.addOrOpenChildRow (L2343)
  // and the legacy `add-edit-inner-grid.html` template.
  // ---------------------------------------------------------------------------

  /**
   * Open the inner-grid popup for a nested cell. The cell carries its own
   * `NestedTable` (built during `buildFormTree`), so the popup is just a
   * second instance of the same table editor.
   *
   * If the nested table has no rows yet (legacy "no child rows" branch in
   * addOrOpenChildRow), seed it with one empty row so the user has
   * something to edit.
   */
  openNestedGrid(
    parentRow: RepeatedRow,
    columnName: string,
    siblingRows: RepeatedRow[] = [],
  ): void {
    const cell = parentRow?.cells?.[columnName];
    if (!cell?.nestedTable) return;
    const nested = cell.nestedTable;

    // Seed a fresh starter row when:
    //   - the table is brand new (no rows ever), OR
    //   - every existing row is soft-deleted (visible-row count is 0
    //     because the user pressed the row-delete icon on every row).
    // The second branch is what makes the grouping overlay show the
    // `0.0` entry on reopen after a delete-all cycle, instead of an
    // empty table body. Without this the modal would re-open empty
    // and the user would have to click "+" to get a row back.
    const visibleCount = nested.repeatedRows.filter(r => !r?.toDelete && !!r?.SNO).length;
    if (visibleCount === 0) {
      const seedRow = this.seedNestedRowFromFirstSibling(parentRow, columnName, siblingRows, nested)
        ?? this.seedNestedRowFromHeaders(parentRow, nested);
      // Preserve repeating-table signals from previously-deleted rows.
      // If the user had grouped rows (e.g. `0.0`, `0.1`) and deleted
      // every one, the deleted rows still carry `IS_REPEATED: true`
      // and the original `MAX_OCCURS`. Without inheriting those onto
      // the new seed, `seedNestedRowFromHeaders`'s hardcoded
      // `IS_REPEATED: false` collapses the modal back to a plain
      // (non-repeating) layout — the SNO column + add icon disappear,
      // which was the user-reported regression.
      this.inheritRepeatingFlagsFromExistingRows(seedRow, nested);
      // Reset SNO to the dotted-prefix start (`{prefix}.0`) ONLY when the
      // table already carried historical rows (typically all soft-deleted
      // after a delete-all cycle). Those legacy rows are the source of
      // the dotted prefix `snoForEmptyTable` is designed to preserve.
      //
      // When the nested table is brand-new (no historical rows), the seed
      // builders (`seedNestedRowFromFirstSibling` /
      // `seedNestedRowFromHeaders`) have ALREADY computed the correct
      // SNO — for max-1 inner columns whose donor SNO is flat, that SNO
      // equals the outer `parentRow.SNO` (e.g. "1"), matching the legacy
      // `addOrOpenChildRow` L246-L247 contract:
      //   `firstRow.SNO = SNOParts.length > 1 ? repData.SNO + '.' + SNOParts[1] : repData.SNO`
      // Re-running `snoForEmptyTable` on the empty-table case was
      // unconditionally collapsing those non-zero flat SNOs back to "0"
      // (the function's flat-fallback returns hardcoded "0"), which is
      // the root cause of the `SAVE_EFILE_ELEMENT_OVERRIDE` regression
      // that shipped `"sno":"0"` for deeply-nested max-1 overlay edits
      // (e.g. `…SupplierName.BusinessNameLine1Txt`).
      const hasHistoricalRows = (nested.repeatedRows?.length ?? 0) > 0;
      const resetSno = hasHistoricalRows
        ? this.snoForEmptyTable(seedRow.SNO, nested.repeatedRows)
        : String(seedRow.SNO ?? '0');
      this.applySnoToRow(seedRow, resetSno);
      nested.repeatedRows.push(seedRow);
      nested.repeatedRowsBySno[seedRow.SNO] = seedRow;
    }

    // Guarantee EVERY inner row exposes a cell for EVERY column header so
    // the template's `*ngIf="getRepeatedCell(r, h) as cell"` doesn't drop
    // the scalar editor on rows that were hydrated via
    // `hydrateNestedTableFromTreeData` (which only seeds cells for the
    // columns it found in `treeData`). Without this, the legacy "few
    // groups don't show any fields" bug appeared whenever a row had data
    // for a subset of columns and the other columns rendered as a
    // non-editable `.empty-cell` placeholder.
    for (const innerRow of nested.repeatedRows) {
      this.ensureRowHasCellsForHeaders(innerRow, nested.tableHeaders || []);
      if (innerRow?.SNO) {
        this.applySnoToRow(innerRow, String(innerRow.SNO));
      }
    }

    this.nestedPopup = {
      parentRow,
      parentColumn: columnName,
      nestedTable: nested,
      title: `${parentRow.SNO} - ${columnName}`,
    };
    this.cdr.markForCheck();
  }

  /**
   * Legacy parity (`addOrOpenChildRow`, EfileElementsController.js L2129-L2156).
   * Locate the first sibling parent row that holds nested data for the
   * same column, deep-clone its first non-empty nested row, blank out
   * every scalar value, and rewrite SNOs so the clone belongs to the
   * current parent. Returns `null` when no sibling row carries data
   * (caller falls back to a header-built placeholder).
   */
  private seedNestedRowFromFirstSibling(
    parentRow: RepeatedRow,
    columnName: string,
    siblingRows: RepeatedRow[],
    nested: NestedTable,
  ): RepeatedRow | null {
    // Step 1: find first sibling with nested data for `columnName`.
    let donorRow: RepeatedRow | null = null;
    for (const other of siblingRows) {
      if (other === parentRow) continue;
      const otherNested = other.cells?.[columnName]?.nestedTable;
      const donorCandidate = otherNested?.repeatedRows?.find(r => !!r && !r.toDelete);
      if (donorCandidate) {
        donorRow = donorCandidate;
        break;
      }
    }
    if (!donorRow) return null;

    // Step 2: clone donor row, blank scalar values, rewrite SNOs.
    // Mirrors legacy:
    //   SNOParts = firstRow.SNO.split('.');
    //   firstRow.SNO = SNOParts.length > 1 ? repData.SNO + '.' + SNOParts[1] : repData.SNO;
    //
    // Donor SNO is "always dotted" in legacy because `createPopupElement`
    // L2633 normalises the stored SNO to `parentSNO.childSNO` whenever
    // `maxOccurs[j] != '1'`. Our `buildFormTree` now applies the same
    // normalisation at the call to `upsertNestedRow`, so a multi-part
    // donor reliably yields `${parentRow.SNO}.${donor[1]}` (e.g. "0.0").
    //
    // The single-part donor branch is preserved for the legacy max-1
    // popup case where the donor row's SNO is just `childSNO` (no dot):
    // here the inner grid is non-repeating, the SNO column is hidden by
    // the `r.IS_REPEATED` guard in the template, and the user has no
    // incremental-add affordance — so we keep the legacy short-form
    // SNO (`repData.SNO`) to match.
    const donorSnoParts = String(donorRow.SNO ?? '').split('.');
    const newSno =
      donorSnoParts.length > 1
        ? `${parentRow.SNO}.${donorSnoParts[1]}`
        : String(parentRow.SNO);

    const cloned: RepeatedRow = safeJsonInput<any>(JSON.stringify(donorRow), null, 'add-edit.parse1');
    cloned.SNO = newSno;
    cloned.isNew = true;
    cloned.toDelete = false;
    // Legacy parity (addOrOpenChildRow EfileElementsController.js L2157-L2158):
    //   (MAX_OCCURS && MAX_OCCURS != "1") && (last.IS_REPEATED = true);
    // Only promote IS_REPEATED to true when the inner-grid is genuinely
    // repeating (TREEMAXOCCURS != "1"). For max-1 inner columns we MUST
    // preserve the donor's IS_REPEATED (which `upsertNestedRow` already
    // set to `false` based on maxOccurs[j] === '1') — otherwise the
    // template renders an unwanted SNO column + `+` add-row icon for
    // columns that the schema only allows once. The previous override
    // (`cloned.IS_REPEATED = true`) clobbered that signal for every
    // seeded row regardless of cap.
    const clonedCap = String(cloned.MAX_OCCURS ?? '');
    if (clonedCap && clonedCap !== '1') {
      cloned.IS_REPEATED = true;
    }

    if (cloned.cells) {
      Object.keys(cloned.cells).forEach(k => {
        const c = cloned.cells![k];
        if (!c) return;
        c.ELEMENTVALUE = '';
        c.SNO = newSno;
        c.IS_CHANGED = undefined;
        // Inherit ENUMVALUES/MAXLENGTH from the column header when present
        // so the cloned cell stays consistent with the popup's column set
        // (legacy headers are also refreshed from the donor row).
        const colHeader = nested.tableHeaders.find(h => h.nodeName === k);
        if (colHeader) {
          c.ENUMVALUES = colHeader.ENUMVALUES;
          c.MAXLENGTH = colHeader.MAXLENGTH;
          c.isRequired = colHeader.isRequired;
        }
      });
    } else {
      cloned.cells = {};
    }
    return cloned;
  }

  /**
   * Fallback seed builder when no sibling carries nested data.
   *
   * We have NO TREEMAXOCCURS signal here (neither headers nor sibling
   * rows carry it), so default `IS_REPEATED` to `false` — matching legacy
   * semantics for max-1 inner-grids (`addOrOpenChildRow` L2157-L2158
   * only promotes IS_REPEATED when MAX_OCCURS != "1"). The previous
   * unconditional `true` produced a spurious SNO column + add-row icon
   * for non-repeating inner-grid columns when first opened without
   * sibling data. If the schema does allow more rows, subsequent
   * hydration via `upsertNestedRow` (driven by treeData's TREEMAXOCCURS)
   * will refresh IS_REPEATED to its correct value on the data-bearing
   * row.
   */
  private seedNestedRowFromHeaders(
    parentRow: RepeatedRow,
    nested: NestedTable,
  ): RepeatedRow {
    const cells: Record<string, RepeatedCell> = {};
    nested.tableHeaders.forEach(h => {
      cells[h.nodeName] = {
        ELEMENTNAME: h.nodeName,
        FULLELEMENTNAME: h.fullElementName,
        ELEMENTVALUE: '',
        SNO: parentRow.SNO,
        MAXLENGTH: h.MAXLENGTH,
        ENUMVALUES: h.ENUMVALUES,
        isRequired: h.isRequired,
      };
    });
    return {
      SNO: parentRow.SNO,
      cells,
      isNew: true,
      IS_REPEATED: false,
    };
  }

  /** Close the inner-grid popup. Edits made inside stay on the model. */
  closeNestedGrid(): void {
    this.nestedPopup = null;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // View Stylesheet — port of AddEditController.viewStyleSheet (L2480).
  // Calls the gtw_efile_api_url '/viewStyleSheet' endpoint, rewrites the
  // legacy `mef/` image paths to point at our assets dir, wraps the HTML
  // in a Blob URL, and opens it in a 780x800 popup window.
  // ---------------------------------------------------------------------------

  /** True while a stylesheet GET is in flight (drives spinner on the button). */
  loadingStylesheet = false;

  viewStyleSheet(): void {
    if (this.loadingStylesheet) return;
    if (!this.formId) {
      this.gtwSnackbarService.openSnackBar(
        'Save a form before viewing its stylesheet.',
        'gtw-snackbar--yellow'
      );
      return;
    }
    if (!this.submissionId) {
      this.gtwSnackbarService.openSnackBar(
        'Submission context is not available yet.',
        'gtw-snackbar--yellow'
      );
      return;
    }

    const efileBase = this.baseURLs?.efile;
    if (!efileBase) {
      this.gtwSnackbarService.openSnackBar(
        'Stylesheet preview is not configured.',
        'gtw-snackbar--red'
      );
      return;
    }

    this.gtwSnackbarService.openSnackBar(
      'Generating Stylesheet. Please wait...',
      'gtw-snackbar--green'
    );
    this.loadingStylesheet = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.addEditService.viewStyleSheet(efileBase, this.submissionId, this.formId).subscribe(
        (response: any) => {
          try {
            // Some backends wrap the HTML in `{ html }`, others return it raw.
            const rawHtml: string =
              (response && (response.html ?? response?.data?.html)) ||
              (typeof response === 'string' ? response : '');

            if (!rawHtml) {
              this.gtwSnackbarService.openSnackBar(
                'Stylesheet response was empty.',
                'gtw-snackbar--red'
              );
              return;
            }

            // Rewrite mef/ asset paths so the popup can resolve the
            // per-year stylesheet bundle (general.css, header.css, body.css,
            // MainStyles.css, etc. that define styBodyClass / styFNBox /
            // styFormNumber / styTBB ...) and the embedded image refs.
            //
            // Match legacy bug-for-bug (EfileElementsController.js L2484–
            // L2486): the original code reassigns `baseUrl` from
            // `GENERAL_CONFIG.base_url` on the second line, silently
            // discarding the `/gateway` strip on the first line. Net effect:
            // only `com/gtw` → `com` actually applies. We mirror that here.
            //
            // Also prefer `GENERAL_CONFIG.base_url` over `baseURLs.api` —
            // the gtw API host is the one that serves
            // /assets/img/efile/mef/<year>/styles/*.css; `baseURLs.api`
            // typically points at the elements bundle host, which doesn't.
            const apiBase = (this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '')
              .replace('com/gtw', 'com');
            const rewritten = rawHtml.replace(/mef\//g, `${apiBase}/assets/img/efile/mef/`);

            const blob = new Blob([rewritten], { type: 'text/html' });
            const winUrl = URL.createObjectURL(blob);
            const popup = window.open(
              winUrl,
              'StylesheetPreview',
              'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=780,height=800'
            );

            if (!popup) {
              this.gtwSnackbarService.openSnackBar(
                'Stylesheet popup was blocked. Allow popups for this site.',
                'gtw-snackbar--yellow'
              );
              URL.revokeObjectURL(winUrl);
            }
          } catch (e) {
            console.error('viewStyleSheet rendering error:', e);
            this.gtwSnackbarService.openSnackBar(
              'Could not render the stylesheet.',
              'gtw-snackbar--red'
            );
          } finally {
            this.loadingStylesheet = false;
            this.cdr.markForCheck();
          }
        },
        (error: any) => {
          console.error('viewStyleSheet error:', error);
          this.gtwSnackbarService.openSnackBar(
            'Failed to generate the stylesheet.',
            'gtw-snackbar--red'
          );
          this.loadingStylesheet = false;
          this.cdr.markForCheck();
        }
      )
    );
  }

  // ---------------------------------------------------------------------------
  // Save flow with pre-save comments modal (legacy askComment → saveForm).
  // ---------------------------------------------------------------------------

  save(): void {
    if (this.saving) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    if (this.isProductionAccepted) {
      this.gtwSnackbarService.openSnackBar(
        'Cannot edit a Production-Accepted submission.', 'gtw-snackbar--yellow'
      );
      return;
    }
    if (this.isSubmissionLocked) {
      this.gtwSnackbarService.openSnackBar(
        this.SUBMISSION_LOCKED_MESSAGE, 'gtw-snackbar--yellow'
      );
      return;
    }
    if (!this.changedFields.length) {
      this.gtwSnackbarService.openSnackBar('No changes found to save!', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.selectedEntity && !this.keyIdFromRow) {
      this.gtwSnackbarService.openSnackBar('Select an entity before saving.', 'gtw-snackbar--yellow');
      return;
    }

    // Pre-save form-validity gate — port of askComment's `addEditForm.$invalid`
    // block (EfileElementsController.js L2271-L2280). Marks every invalid cell
    // as touched so the legacy red `fa-info-circle` hints become visible, then
    // bails with the legacy warn message instead of opening the comments modal.
    if (this.markInvalidFieldsTouched()) {
      this.gtwSnackbarService.openSnackBar(
        'Please fix the data errors shown below!', 'gtw-snackbar--yellow'
      );
      return;
    }

    // Open comments modal — legacy AddEditController.askComment.
    this.showCommentsModal = true;
    this.cdr.markForCheck();
  }

  unlockForm(): void {
    this.lockEdit = false;
    this.cdr.markForCheck();
  }

  onCancelCommentsModal(): void {
    this.showCommentsModal = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  /** Wired to {@link CommentsModalComponent} `request-grid-update`: comment string or `'cancel'`. */
  onCommentsModalRequest(detail: any): void {
    if (detail === 'cancel') {
      this.onCancelCommentsModal();
      return;
    }
    const comment = typeof detail === 'string' ? detail : '';
    this.showCommentsModal = false;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    this.runSave(comment);
  }

  private runSave(comment: string): void {
    this.saving = true;
    this.cdr.markForCheck();

    const args: AddEditSaveArgs = {
      submissionId: this.submissionId,
      keyId: this.currentKeyId,
      form: this.selectedForm,
      formSub: this.selectedFormSub,
      parentElementName: this.selectedParentElement,
      formId: this.formId,
      comment,
      changes: this.changedFields,
      taxYear: this.taxYear,
      scenario: this.getScenario(),
      jcdKey: JCD_KEY_DEFAULT,
      ssoId: this.ssoId,
    };

    const baseUrl = this.GENERAL_CONFIG.base_url || this.baseURLs.api;
    // Capture the pre-save formId so we can decide whether the saved form
    // is brand-new (never-before-persisted, formId still empty) and the
    // tile list needs a refresh on success — port of legacy
    // EfileElementsController.js L2373-L2377:
    //   if (vm.formId.length > 0) vm.loadForm();
    //   else                      vm.loadForms().then(()=>vm.loadForm());
    const wasNewForm = !this.formId;
    this.addEditService.saveFormData(baseUrl, args).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.saving = false;
        const ok = response?.callSuccess === '1' || response?.callSuccess === 1;
        if (!ok) {
          this.gtwSnackbarService.openSnackBar(
            response?.errorMessage || 'Something went wrong while saving the data!',
            'gtw-snackbar--red'
          );
          this.cdr.markForCheck();
          return;
        }
        this.changedFields = [];
        this.gtwSnackbarService.openSnackBar('Data saved successfully!', 'gtw-snackbar--green');
        // Auto-lock the editor after a successful save — direct port of
        // legacy `vm.lockEdit = true;` (controller L2380). Forces the
        // user to press Unlock before the next round of edits, which
        // also fits the `IS_LOCKED`/`isSubmissionLocked` UX where Save
        // is hidden once the row commits.
        this.lockEdit = true;
        if (wasNewForm) {
          // First-save of a brand-new form: re-fetch the tile list so the
          // new form actually shows up under the entity's icons before
          // we reload the form payload itself.
          this.loadFormsForEntity();
        }
        this.loadFormData();
        this.cdr.markForCheck();
      },
      (err: any) => {
        console.error('[EfileAddEdit] save failed', err);
        this.saving = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------

  trackByKey(_i: number, n: EntityNode): any {
    return n.KEY_ID ?? n.KEY_DESC;
  }

  trackByForm(_i: number, f: any): any {
    return f.FORM || f.form;
  }

  trackByTreeRow(_i: number, r: FormTreeRow): any {
    return r.FULLELEMENTNAME + ':' + r.level + ':' + r.kind;
  }

  trackBySno(_i: number, r: RepeatedRow): any {
    return r.SNO;
  }

  trackByHeader(_i: number, h: TableHeader): any {
    return h.fullElementName;
  }

  /**
   * Tile header (`addEdit.html` L446): show “Add form” (+) when schema forms exist.
   */
  shouldShowToolbarAdd(): boolean {
    if (!(this.selectedEntity || this.keyIdFromRow)) return false;
    if (this.loadingForms) return false;
    return (this.allForms?.length || 0) > 0;
  }

  /**
   * Tile header (`addEdit.html` L447): show search when the entity has forms.
   */
  shouldShowToolbarSearch(): boolean {
    if (!(this.selectedEntity || this.keyIdFromRow)) return false;
    if (this.loadingForms) return false;
    return (this.uniqForms?.length || 0) > 0;
  }

  /** OnPush: `filteredUniqForms` is a getter — re-check after search typing. */
  onFormSearchInput(): void {
    this.cdr.markForCheck();
  }

  get filteredUniqForms(): any[] {
    const list = this.uniqForms || [];
    const term = (this.formSearchTxt || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((f: any) => String(f?.FORM || f?.form || '').toLowerCase().includes(term));
  }

  /**
   * Options bound to the Form typeahead in the filter strip
   * (`gtw-efile-typeahead-select` `[options]="formOptionsForEditor"`).
   *
   * IMPORTANT: must source from `uniqForms` — NOT `filteredUniqForms` —
   * so that text typed into the tile-grid search box
   * (`.tile-grid-search.search-container` → `formSearchTxt`) only narrows
   * the tile rail, never the typeahead dropdown. Previously this getter
   * returned `filteredUniqForms` in edit mode, which meant any search
   * term entered in the tile grid would silently shrink the Form
   * typeahead's option list as well — a coupling the user explicitly
   * flagged. The typeahead has its own internal filter, so feed it the
   * full set and let it handle filtering independently.
   */
  get formOptionsForEditor(): any[] {
    const options = this.addMode ? this.allForms : this.uniqForms;
    return _.uniqBy(options || [], (f: any) => String(f?.FORM || f?.form || ''));
  }

  isFieldEditable(node: any): boolean {
    if (this.isReadOnly) return false;
    return node?.IS_EDITABLE !== 'N';
  }

  /**
   * True when the editor must be fully read-only — i.e. the submission is
   * Production-Accepted, submission-level IS_LOCKED, OR the form-source
   * lock returned by `7bkpvk` is on. Mirrors the legacy guard chain that
   * gates Save/Unlock visibility (addEdit.html L560-L564) and the
   * `read-only` class on `.data-row-container` (L571).
   */
  get isReadOnly(): boolean {
    return this.isProductionAccepted || this.lockEdit || this.isSubmissionLocked;
  }

  isEnumField(node: any): boolean {
    return Array.isArray(node?.ENUMVALUES) && node.ENUMVALUES.length > 0;
  }
}
